import React, { useState, useEffect, useRef } from 'react';
import { 
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  IconButton,
  SelectChangeEvent,
  Menu,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItem,
  Divider,
  Popover,
  Alert,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import ResponsiblePersonDisplay from '../components/projects/ResponsiblePersonDisplay';
import { useNotification } from '../components/layouts/Layout';
import { 
  Search, 
  KeyboardArrowDown, 
  MoreVert, 
  CloudDownload, 
  DriveFileMove, 
  OpenInNew,
  ChevronRight,
  ArrowBack,
  Settings,
  CalendarToday,
} from '@mui/icons-material';
import EditIcon from '../components/icons/EditIcon';
import RulerIcon from '../components/icons/RulerIcon';
import { Close, DragIndicator, PlayArrow, Pause, Speed } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import HlsPlayer from '../components/common/HlsPlayer';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AddProjectForm from '../components/projects/AddProjectForm';
import AddOrderForm from '../components/projects/AddOrderForm';
import GeneratePocrDialog from '../components/projects/GeneratePocrDialog';
import GeneratePokrDialog from '../components/projects/GeneratePokrDialog';
import SubcontractorDetailsDialog from '../components/projects/SubcontractorDetailsDialog';
import subcontractorService, { Subcontractor } from '../services/subcontractorService';
import ProjectDetails from '../components/projects/ProjectDetails';
import ImportProjectsDialog from '../components/projects/ImportProjectsDialog';
import * as projectService from '../services/projectService';
import { Project } from '../services/projectService';
import useIsMobile from '../hooks/useIsMobile';
import DriveLogo from '../assets/images/google-drive.svg';
import MobilePackagesList from '../components/projects/MobilePackagesList';
import MobileInquiriesList from '../components/projects/MobileInquiriesList';
import MobileScheduleTab from '../components/projects/MobileScheduleTab';
import MobileOrderPlanList from '../components/projects/MobileOrderPlanList';
import serviceService from '../services/serviceService';
import { useAuth } from '../contexts/AuthContext';
import userService, { AppUser } from '../services/userService';
import { getBoardUrlForProject, checkBoardsExist } from '../services/collectifileService';
import { useProjectCameraHlsUrls } from '../hooks/useProjectCameraHlsUrls';
import { resolveProjectIdForHardwareQuery } from '../utils/projectCameraStreams';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

// Rozszerzony interfejs projektu dla danych wyświetlanych w tabeli
export interface ProjectDisplayData extends Project {
  days: number;
  progress: number;
  wwp: number;
  wwh: number;
  sales: number;
  pozDz: number;
  costs: number;
  profit: number;
  ros: number;
  // Pola dla kompatybilności z istniejącym kodem
  id: string;
  name: string;
  manager: string;
  startDate: string;
  endDate: string;
  planBud: number;
  driveLink?: string;
  miroLink?: string;
  folderId?: string; // ID folderu na Google Drive
}

// Funkcja do generowania inicjałów z pełnego imienia i nazwiska
const getInitials = (fullName: string): string => {
  if (!fullName) return '';
  
  // Dzielimy pełne imię i nazwisko na słowa
  const words = fullName.trim().split(/\s+/);
  
  // Jeśli mamy tylko jedno słowo, zwracamy pierwsze dwie litery
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  
  // W przeciwnym razie zwracamy inicjały (pierwsze litery każdego słowa)
  return words.map(word => word.charAt(0).toUpperCase()).join('');
};

// Funkcja do konwersji Project na ProjectDisplayData
const convertProjectToDisplayData = (project: Project): ProjectDisplayData => {
  // Helper konwersji DECIMAL/string -> number
  const toNumber = (v: any, fallback = 0): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const parsed = parseFloat(v);
      return Number.isNaN(parsed) ? fallback : parsed;
    }
    return fallback;
  };

  // Złóż obiekty odpowiedzialnych na podstawie pól z JOIN (resp*_...) lub fallback do istniejących danych
  const rp1 = (project as any).resp1_user_id || (project as any).resp1_display_name || (project as any).resp1_email
    ? {
        user_id: (project as any).resp1_user_id || (project as any).responsible_person1,
        display_name: (project as any).resp1_display_name,
        email: (project as any).resp1_email,
        first_name: (project as any).resp1_first_name,
        last_name: (project as any).resp1_last_name,
        avatar_url: (project as any).resp1_avatar_url,
      }
    : undefined;
  const rp2 = (project as any).resp2_user_id || (project as any).resp2_display_name || (project as any).resp2_email
    ? {
        user_id: (project as any).resp2_user_id || (project as any).responsible_person2,
        display_name: (project as any).resp2_display_name,
        email: (project as any).resp2_email,
        first_name: (project as any).resp2_first_name,
        last_name: (project as any).resp2_last_name,
        avatar_url: (project as any).resp2_avatar_url,
      }
    : undefined;

  // Obliczenie liczby dni między datami
  const start = project.start_date ? new Date(project.start_date) : new Date();
  const end = project.end_date ? new Date(project.end_date) : start;
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Upewniamy się, że wartości liczbowe są liczbami (Sequelize DECIMAL może zwrócić string)
  const value = toNumber(project.value, 0);
  const budget = toNumber(project.budget, 0);
  const sales = toNumber(project.sales, value);
  const costs = toNumber(project.costs, budget * 0.8);
  const pdzRaw = toNumber(project.PDZ, Math.max(0, value - sales));
  const pozDz: number = Math.round(pdzRaw / 1000); // PDZ (w tys.)
  const profit: number = sales - costs; // EBIDTA (Sprzedaż minus Koszty)
  const ros: number = sales !== 0 ? Math.round((profit / sales) * 100) : 0; // C.ROS (EBIDTA podzielona przez Sprzedaż razy 100%)
  
  return {
    ...project,
    // Osoby odpowiedzialne (na potrzeby UI)
    responsiblePerson1: rp1,
    responsiblePerson2: rp2,
    // Pola dla kompatybilności z istniejącym kodem
    id: `${project.project_id} ${project.project_name}`,
    name: project.project_name,
    manager: (
      rp2?.display_name ||
      (rp2 ? `${rp2.first_name || ''} ${rp2.last_name || ''}`.trim() : '') ||
      ''
    ),
    startDate: project.start_date,
    endDate: project.end_date,
    planBud: project.budget,
    driveLink: project.google_drive_link,
    miroLink: project.miro_link,
    // Wyciągnij ID folderu z linku Google Drive
    folderId: project.google_drive_link ? 
      project.google_drive_link.match(/\/folders\/([^\/\?]+)/)?.[1] || 
      project.google_drive_link.match(/\/file\/d\/([^\/\?]+)/)?.[1] || 
      undefined : undefined,
    // Pola wyliczane
    days,
    progress: 100, // Przykładowa wartość
    wwp: 100, // Przykładowa wartość
    wwh: 75, // Przykładowa wartość
    sales,
    pozDz, // PDZ w tys.
    costs,
    profit,
    ros
  };
};

const ProjectsPage: React.FC = () => {
  const isMobile = useIsMobile(768);
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [manager, setManager] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [users, setUsers] = useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const navigate = useNavigate();
  const [openAddForm, setOpenAddForm] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [projectsData, setProjectsData] = useState<ProjectDisplayData[]>([]);
  const isUnitrescoUser = user?.email?.toLowerCase().endsWith('@unitresco.com');
  const [projectIdsWithBoard, setProjectIdsWithBoard] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectDisplayData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionProjectId, setActionProjectId] = useState<string>('');
  const [phaseMenuAnchorEl, setPhaseMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [phaseChangeProjectId, setPhaseChangeProjectId] = useState<string>('');
  const { showNotification } = useNotification();
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [sheetView, setSheetView] = useState<'main'|'contracting'|'details'|'packages'|'inquiries'|'materials'|'services'|'subcontractors'|'orderplan'|'schedule'>('main');
  const [selectedProjectMobile, setSelectedProjectMobile] = useState<ProjectDisplayData | null>(null);
  // Bottom sheet stała wysokość
  const SHEET_HEIGHT = '80vh';
  // Załaduj listę użytkowników do filtra „Kierownik projektu”
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await userService.listUsersPublicForBusinessForms();
        if (mounted) setUsers(list);
      } catch (e) {
        // pomiń błąd – filtr zadziała bez listy
      }
    })();
    return () => { mounted = false; };
  }, []);
  useEffect(() => {
    if (!isUnitrescoUser || projectsData.length === 0) return;
    const ids = projectsData.map((p) => p.project_id).filter(Boolean);
    if (ids.length === 0) return;
    checkBoardsExist(ids).then((r) => setProjectIdsWithBoard(new Set(r.projectIds)));
  }, [isUnitrescoUser, projectsData]);
  // Styl nagłówków kolumn i ikona sortowania 12x12 z marginesem 4px
  const sortLabelBaseSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px'
    }
  } as const;
  const ProjectSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? ProjectSortIcon : MutedSortIcon) as any,
    sx: { 
      ...sortLabelBaseSx, 
      '& .MuiTableSortLabel-icon': { 
        ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'], 
        color: isActive ? '#2D2D32' : '#d4d4d9' 
      } 
    }
  }) as const;
  const [pkgCount, setPkgCount] = useState(0);
  const [inqCount, setInqCount] = useState(0);
  const [offCount, setOffCount] = useState(0);
  const [ordCount, setOrdCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [materials, setMaterials] = useState<any[]>([]);
  const [materialsSearch, setMaterialsSearch] = useState('');
  const [selectedMaterials, setSelectedMaterials] = useState<any[]>([]);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const [orderContractor, setOrderContractor] = useState<string>('');
  const [orderContent, setOrderContent] = useState<string>('');
  // Kamera – panel boczny z prawej połowy ekranu
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraProject, setCameraProject] = useState<ProjectDisplayData | null>(null);
  const openCam = React.useCallback((p?: ProjectDisplayData | null) => {
    try { console.log('[Projects] openCam', p?.id || null); } catch {}
    setCameraProject(p || null);
    setCameraOpen(true);
  }, []);
  const camHardwareQueryId = resolveProjectIdForHardwareQuery(cameraProject);
  const {
    urls: cameraMagazynUrls,
    loading: cameraHardwareLoading,
    hasHardwareCameras,
    hardwareDevices: cameraHardwareDevices,
  } = useProjectCameraHlsUrls(cameraOpen, camHardwareQueryId);
  const cameraStreamUrls = React.useMemo(
    () => cameraMagazynUrls.filter((u) => String(u || '').trim()),
    [cameraMagazynUrls]
  );
  const cameraGridCols = cameraStreamUrls.length <= 1 ? 1 : 2;
  /** Nowa instancja hls.js przy każdym otwarciu panelu / zmianie projektu — unika „zamarzniętego” kadru. */
  const [cameraPlayerEpoch, setCameraPlayerEpoch] = React.useState(0);
  React.useEffect(() => {
    if (!cameraOpen) return;
    setCameraPlayerEpoch((e) => e + 1);
  }, [cameraOpen, camHardwareQueryId]);
  // Kamera – stan trybu i menu
  const [camMode, setCamMode] = useState<'live'|'playback'>('live');
  const [modeMenuAnchor, setModeMenuAnchor] = useState<null | HTMLElement>(null);
  const [camDate, setCamDate] = useState<string>('');
  const camDateRef = React.useRef<HTMLInputElement | null>(null);
  const [isCamPlaying, setIsCamPlaying] = useState<boolean>(false);
  const [camSpeed, setCamSpeed] = useState<number>(1);
  const [camMinute, setCamMinute] = useState<number>(0); // 0..1440
  const cycleSpeed = () => {
    const speeds = [0.25, 0.5, 1, 1.5, 2, 3, 4];
    const idx = speeds.indexOf(camSpeed);
    setCamSpeed(speeds[(idx + 1) % speeds.length]);
  };

  const formatMinute = (m: number) => {
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // Zamknij panel kamer klawiszem ESC
  useEffect(() => {
    if (!cameraOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCameraOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cameraOpen]);

  // Na żywo: data „dzisiaj” odświeżana co minutę (pole type=date nie pokazuje godziny — czas na obrazie to OSD kamery).
  useEffect(() => {
    if (!cameraOpen) return;
    if (camMode !== 'live') {
      setCamDate('');
      return;
    }
    const applyDate = () => {
      const d = new Date();
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setCamDate(iso);
    };
    applyDate();
    const id = window.setInterval(applyDate, 60_000);
    return () => clearInterval(id);
  }, [camMode, cameraOpen]);

  // Debug: global helper do otwierania panelu z konsoli
  useEffect(() => {
    (window as any).__openCam = () => openCam(null);
    try { console.log('[Projects] cameraOpen state ->', cameraOpen); } catch {}
  }, [openCam, cameraOpen]);
  // Mobile subcontractors
  const [subs, setSubs] = useState<Subcontractor[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSub, setSelectedSub] = useState<Subcontractor | null>(null);
  const [pocrOpen, setPocrOpen] = useState(false);
  const [pokrOpen, setPokrOpen] = useState(false);
  const [subDetailsOpen, setSubDetailsOpen] = useState(false);
  // Usługi (mobile)
  const [services, setServices] = useState<any[]>([]);
  const [servicesSearch, setServicesSearch] = useState('');

  // Refs do pól daty – do otwierania natywnego selektora
  const startDateRef = React.useRef<HTMLInputElement | null>(null);
  const endDateRef = React.useRef<HTMLInputElement | null>(null);

  // Tryb zmiany szerokości kolumn oraz zapamiętywanie szerokości per użytkownik
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    sel: 48,
    project: 260,
    kp: 26,
    days: 26,
    progress: 34,
    wwp: 34,
    wwh: 34,
    value: 50,
    sales: 50,
    pdz: 36,
    budget: 42,
    costs: 42,
    profit: 42,
    ros: 40,
    drive: 34,
    miro: 26,
    board: 26,
    action: 30,
  }), []);
  const STORAGE_KEY = React.useMemo(() => `projectsTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const WIDTHS_KEY = React.useMemo(() => `${STORAGE_KEY}:widths`, [STORAGE_KEY]);
  const VISIBLE_KEY = React.useMemo(() => `${STORAGE_KEY}:visible`, [STORAGE_KEY]);
  const ORDER_KEY = React.useMemo(() => `${STORAGE_KEY}:order`, [STORAGE_KEY]);
  const STORAGE_KEY_GLOBAL = 'projectsTableColWidths';
  const WIDTHS_KEY_GLOBAL = `${STORAGE_KEY_GLOBAL}:widths`;
  const VISIBLE_KEY_GLOBAL = `${STORAGE_KEY_GLOBAL}:visible`;
  const ORDER_KEY_GLOBAL = `${STORAGE_KEY_GLOBAL}:order`;
  const ALL_COLUMNS: string[] = React.useMemo(() => ['project','kp','days','progress','wwp','wwh','value','sales','pdz','budget','costs','profit','ros','drive','camera','ruler','action'], []);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('projectsTableColWidths:order');
      const ALL = ['project','kp','days','progress','wwp','wwh','value','sales','pdz','budget','costs','profit','ros','drive','camera','ruler','action'];
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) {
          return ord.filter((k: string)=>ALL.includes(k)).concat(ALL.filter(k=>!ord.includes(k)));
        }
      }
    } catch {}
    return ['project','kp','days','progress','wwp','wwh','value','sales','pdz','budget','costs','profit','ros','drive','camera','ruler','action'];
  });
  const [orderLoaded, setOrderLoaded] = useState<boolean>(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(defaultColumnWidths);
  const [widthsFromStorage, setWidthsFromStorage] = useState<boolean>(false);
  const [widthsInitialized, setWidthsInitialized] = useState<boolean>(false);
  const settingsReady = widthsInitialized && orderLoaded;
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [tableHeight, setTableHeight] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!settingsReady) return;
    const measureHeights = () => {
      try {
        const root = tableContainerRef.current as HTMLElement | null;
        if (!root) return;
        const headerRow = root.querySelector('thead tr') as HTMLElement | null;
        const bodyRows = root.querySelectorAll('tbody tr:not([data-sum-row="true"])');
        const sumRow = root.querySelector('tbody tr[data-sum-row="true"]') as HTMLElement | null;
        const headerH = Math.round(headerRow?.getBoundingClientRect().height || 32);
        // policz MAKSYMALNĄ wysokość z pierwszych kilku wierszy (lepsza gwarancja 9 pełnych wierszy)
        let rowH = 36;
        const sampleCount = Math.min(12, bodyRows.length);
        if (sampleCount > 0) {
          let maxH = 0;
          for (let i = 0; i < sampleCount; i++) {
            const h = (bodyRows[i] as HTMLElement).getBoundingClientRect().height;
            if (h && h > maxH) maxH = h;
          }
          if (maxH > 0) rowH = Math.round(maxH);
        }
        const sumH = Math.round(sumRow?.getBoundingClientRect().height || rowH);
        const borders = 2;
        const fudgePx = 20; // delikatny zapas
        const heightForNineRows = headerH + (rowH * 9) + sumH + borders + fudgePx;
        // dostępna przestrzeń do dołu okna – nigdy nie zmniejszajmy poniżej niej
        const top = root.getBoundingClientRect().top;
        const viewportAvail = Math.max(300, Math.floor((window.innerHeight || 0) - top - 16));
        const finalHeight = Math.max(heightForNineRows, viewportAvail);
        setTableHeight(finalHeight);
      } catch {}
    };
    // pierwsze obliczenie po renderze
    const t = setTimeout(measureHeights, 0);
    const t2 = setTimeout(measureHeights, 100);
    window.addEventListener('resize', measureHeights);
    return () => { clearTimeout(t); clearTimeout(t2); window.removeEventListener('resize', measureHeights); };
  }, [settingsReady]);
  // Domyślnie ukryj WWP, WWH i ROS – użytkownik może je włączyć w "Dostosuj kolumny"
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>({ wwp: false, wwh: false, ros: false });
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsAnchorEl, setColsAnchorEl] = useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [colsPosition, setColsPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = useState<{ startX: number; startY: number; top: number; left: number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 474 } as const;
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);

  const getColWidthPx = (key: string): string => {
    const def = defaultColumnWidths[key] ?? 40;
    const cur = columnWidths[key];
    const width = typeof cur === 'number' ? Math.max(cur, def) : def;
    return `${width}px`;
  };

  const isResizingRef = React.useRef(false);
  const appliedOnceRef = React.useRef(false);

  // Po pełnej inicjalizacji – twardo przyłóż zapisane w LS kolejność i szerokości, jeśli różnią się od aktualnych
  useEffect(() => {
    if (!settingsReady || appliedOnceRef.current) return;
    try {
      const savedOrderRaw = localStorage.getItem(ORDER_KEY) || localStorage.getItem(ORDER_KEY_GLOBAL);
      if (savedOrderRaw) {
        const so = JSON.parse(savedOrderRaw);
        if (Array.isArray(so) && so.length) {
          const normalized = so.filter((k: string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!so.includes(k)));
          if (JSON.stringify(normalized) !== JSON.stringify(columnOrder)) {
            setColumnOrder(normalized);
          }
        }
      }
      const savedWidthsRaw = localStorage.getItem(WIDTHS_KEY) || localStorage.getItem(WIDTHS_KEY_GLOBAL);
      if (savedWidthsRaw) {
        const sw = JSON.parse(savedWidthsRaw);
        if (sw && typeof sw === 'object') {
          const next = { ...columnWidths } as Record<string, number>;
          let changed = false;
          Object.keys(sw).forEach((k) => {
            const v = sw[k];
            if (typeof v === 'number' && next[k] !== v) { next[k] = v; changed = true; }
          });
          if (changed) setColumnWidths(next);
        }
      }
      appliedOnceRef.current = true;
    } catch {}
  }, [settingsReady, ORDER_KEY, ORDER_KEY_GLOBAL, WIDTHS_KEY, WIDTHS_KEY_GLOBAL, ALL_COLUMNS, columnOrder, columnWidths]);

  // Domyślne szerokości kolumn (w % szerokości tabeli) dla trybu bez edycji,
  // dobrane tak, aby nie pojawiał się suwak poziomy i cała szerokość była wykorzystana.
  const nonResizePercentWidth: Record<string, number> = {
    project: 28,
    kp: 4,
    days: 4,
    progress: 5,
    wwp: 5,
    wwh: 5,
    value: 7,
    sales: 7,
    pdz: 7,
    budget: 7,
    costs: 7,
    profit: 7,
    ros: 5,
    drive: 3,
    camera: 2,
    ruler: 2,
    action: 3,
  };

React.useLayoutEffect(() => {
    try {
      // Wczytaj szerokości (v2), z fallbackiem do starego klucza
      let raw = localStorage.getItem(WIDTHS_KEY) || localStorage.getItem(WIDTHS_KEY_GLOBAL) || localStorage.getItem(STORAGE_KEY);
      // Ostatnia deska ratunku: jeśli brak, spróbuj znaleźć dowolny klucz projectsTableColWidths:*:widths
      if (!raw) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i) || '';
          if (k.startsWith('projectsTableColWidths:') && (k.endsWith(':widths') || (!k.includes(':') && k !== STORAGE_KEY))) {
            const candidate = localStorage.getItem(k);
            if (candidate) { raw = candidate; break; }
          }
        }
      }
      // Migracja z klucza 'anon' jeśli użytkownik jest już znany
      if (!raw && user?.user_id) {
        const anonRaw = localStorage.getItem('projectsTableColWidths:anon');
        if (anonRaw) {
          raw = anonRaw;
          try { localStorage.setItem(WIDTHS_KEY, anonRaw); localStorage.setItem(STORAGE_KEY, anonRaw); } catch {}
        }
      }
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setColumnWidths(prev => ({ ...prev, ...parsed }));
          setWidthsFromStorage(true);
        }
      }
      let visRaw = localStorage.getItem(VISIBLE_KEY) || localStorage.getItem(VISIBLE_KEY_GLOBAL);
      if (!visRaw && user?.user_id) {
        const anonVis = localStorage.getItem(`projectsTableColWidths:anon:visible`);
        if (anonVis) {
          visRaw = anonVis;
          try { localStorage.setItem(VISIBLE_KEY, anonVis); } catch {}
        }
      }
      if (!visRaw) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i) || '';
          if (k.startsWith('projectsTableColWidths:') && k.endsWith(':visible')) {
            const candidate = localStorage.getItem(k);
            if (candidate) { visRaw = candidate; break; }
          }
        }
      }
      if (visRaw) {
        const parsed = JSON.parse(visRaw);
        if (parsed && typeof parsed === 'object') setColumnsVisible(parsed);
      }
      // Kolejność kolumn (z migracją z 'anon')
      let ordRaw = localStorage.getItem(ORDER_KEY) || localStorage.getItem(ORDER_KEY_GLOBAL);
      if (!ordRaw && user?.user_id) {
        const anonOrder = localStorage.getItem('projectsTableColWidths:anon:order');
        if (anonOrder) {
          ordRaw = anonOrder;
          try { localStorage.setItem(ORDER_KEY, anonOrder); } catch {}
        }
      }
      if (!ordRaw) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i) || '';
          if (k.startsWith('projectsTableColWidths:') && k.endsWith(':order')) {
            const candidate = localStorage.getItem(k);
            if (candidate) { ordRaw = candidate; break; }
          }
        }
      }
      if (ordRaw) {
        const ord = JSON.parse(ordRaw);
        if (Array.isArray(ord) && ord.length) setColumnOrder(ord.filter((k: string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!ord.includes(k))));
      }
      setOrderLoaded(true);
    } catch {}
}, [STORAGE_KEY, WIDTHS_KEY, VISIBLE_KEY, ORDER_KEY, ALL_COLUMNS]);

  // Zainicjalizuj szerokości kolumn na podstawie procentów, jeśli nie ma wartości z localStorage
  useEffect(() => {
    if (widthsInitialized) return;
    if (widthsFromStorage) { setWidthsInitialized(true); return; }
    const el = tableContainerRef.current;
    const containerWidth = el ? el.clientWidth : (typeof window !== 'undefined' ? Math.max(900, Math.floor(window.innerWidth * 0.9)) : 1200);
    const computed: Record<string, number> = { ...columnWidths };
    Object.entries(nonResizePercentWidth).forEach(([key, pct]) => {
      computed[key] = Math.max(30, Math.round((pct / 100) * containerWidth));
    });
    setColumnWidths(computed);
    setWidthsInitialized(true);
  }, [widthsFromStorage, widthsInitialized]);

  useEffect(() => {
    if (!widthsInitialized) return;
    try {
      const payload = JSON.stringify(columnWidths);
      localStorage.setItem(WIDTHS_KEY, payload);
      localStorage.setItem(STORAGE_KEY, payload); // kompatybilność wstecz
      localStorage.setItem(WIDTHS_KEY_GLOBAL, payload); // globalny klucz
    } catch {}
  }, [STORAGE_KEY, WIDTHS_KEY, WIDTHS_KEY_GLOBAL, columnWidths, widthsInitialized]);

  useEffect(() => {
    try {
      const payload = JSON.stringify(columnsVisible);
      localStorage.setItem(VISIBLE_KEY, payload);
      localStorage.setItem(VISIBLE_KEY_GLOBAL, payload);
    } catch {}
  }, [VISIBLE_KEY, VISIBLE_KEY_GLOBAL, columnsVisible]);

  useEffect(() => {
    if (!orderLoaded) return;
    // Zapisuj tylko gdy rzeczywiście zmieniliśmy kolejność względem ALL_COLUMNS lub poprzedniej
    try {
      const payload = JSON.stringify(columnOrder);
      const prev = localStorage.getItem(ORDER_KEY_GLOBAL);
      if (prev !== payload) {
        localStorage.setItem(ORDER_KEY, payload);
        localStorage.setItem(ORDER_KEY_GLOBAL, payload);
      }
    } catch {}
  }, [ORDER_KEY, ORDER_KEY_GLOBAL, columnOrder, orderLoaded]);

  // Odkładamy zapis widths po pierwszym przyłożeniu odczytanych wartości, aby uniknąć ich nadpisania przez defaulty
  const firstPaintRef = React.useRef(true);
  useEffect(() => {
    if (!widthsInitialized) return;
    if (firstPaintRef.current) { firstPaintRef.current = false; return; }
    try {
      const payload = JSON.stringify(columnWidths);
      localStorage.setItem(WIDTHS_KEY, payload);
      localStorage.setItem(STORAGE_KEY, payload);
      localStorage.setItem(WIDTHS_KEY_GLOBAL, payload);
    } catch {}
  }, [WIDTHS_KEY, STORAGE_KEY, WIDTHS_KEY_GLOBAL, columnWidths, widthsInitialized]);

  const saveWidths = React.useCallback(() => {
    try {
      const payload = JSON.stringify(columnWidths);
      localStorage.setItem(WIDTHS_KEY, payload);
      localStorage.setItem(STORAGE_KEY, payload);
    } catch {}
  }, [WIDTHS_KEY, STORAGE_KEY, columnWidths]);

  // Przeciąganie okna "Dostosuj kolumny"
  useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [colsDragging]);

  // (Usunięto automatyczne sprzęganie z oknem) – tryb resize sterowany ikoną i może otworzyć okno w drugim kroku

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      isResizingRef.current = true;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => { e.preventDefault(); setDragCol(null); isResizingRef.current = false; saveWidths(); };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol, saveWidths]);

  // ESC wychodzi z trybu dostosowania kolumn (zamyka okno i linie pomocnicze)
  useEffect(() => {
    if (!(colResizeMode || colsOpen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDragCol(null);
        setColResizeMode(false);
        setColsAnchorEl(null);
        setDraftColumnsVisible(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colResizeMode, colsOpen]);

  // ESC zamyka okno „Dostosuj kolumny" (bez blokowania reszty)
  useEffect(() => {
    if (!colsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setColsAnchorEl(null);
        setDraftColumnsVisible(null);
        setColResizeMode(false);
        setDragCol(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colsOpen]);

  // Fallback: zapisuj również przy każdej stabilizacji zmian szerokości
  useEffect(() => {
    if (!widthsInitialized || isResizingRef.current) return;
    const id = setTimeout(() => { saveWidths(); }, 250);
    return () => clearTimeout(id);
  }, [columnWidths, widthsInitialized, saveWidths]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell
      align={align}
      sx={{
        ...cellStyle,
        maxWidth: 'none',
        width: getColWidthPx(keyId),
        position: 'relative',
        // Usuń pionowe separatory: pomiędzy ROS|Drive, Drive|Camera, Camera|Ruler, Ruler|Action
        ...( ['ros','drive','camera','ruler'].includes(keyId) ? { borderRight: '1px solid transparent !important' } : {} ),
        userSelect: 'none'
      }}
    >
      {keyId === 'project' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node}
      {(colResizeMode) && (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            setDragCol({ 
              key: keyId, 
              startX: e.clientX, 
              startW: parseInt(getColWidthPx(keyId)) || (columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40) 
            }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '12px',
            minHeight: '100%',
            cursor: 'col-resize',
            zIndex: 15,
            touchAction: 'none',
            pointerEvents: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              transform: 'translateX(-50%)',
              backgroundColor: '#FF0000',
              zIndex: 16
            },
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&::before': {
                width: '3px',
                backgroundColor: '#CC0000'
              }
            }
          }}
        />
      )}
    </TableCell>
  );

  const renderColGroup = (order: string[]) => (
    <colgroup>
      {order.map((key) => (
        colV(key) ? <col key={`col-${key}`} style={{ width: getColWidthPx(key) }} /> : null
      ))}
    </colgroup>
  );

  // Render komórki danych dla głównej tabeli (widok 1)
  const renderDataCellMain = (keyId: string, project: any, index: number) => {
    switch (keyId) {
      case 'project':
        const projectId = String((project as any).project_id || project.id);
        return (
          <TableCell 
            key={`c-${keyId}-${index}`} 
            sx={{ 
              ...cellStyle, 
              whiteSpace: 'normal', 
              overflow: 'visible', 
              textOverflow: 'unset', 
              maxWidth: 'none', 
              ...cellSx('project')
            }}
          >
            <Box sx={{ pl: '16px' }}>
              <RouterLink 
                to={`/projects/${encodeURIComponent(projectId)}`}
                onClick={(e) => e.stopPropagation()}
                style={{
                  textDecoration: 'underline',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#3F3FDC',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block'
                }}
              >
                {project.id}
              </RouterLink>
              <Box sx={{ mt: 0.25, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#595966' }}>
                {`${project.startDate || '—'} — ${project.endDate || '—'}`}
              </Box>
            </Box>
          </TableCell>
        );
      case 'kp':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, ...cellSx('kp') }}>
            <ResponsiblePersonDisplay person={project.responsiblePerson2 || project.responsible_person2} label="Kierownik projektu" />
          </TableCell>
        );
      case 'days':
        return (<TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('days') }}>{project.days}</TableCell>);
      case 'progress':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', ...cellSx('progress') }}>
            <Box sx={{ color: Number(project.progress) >= 75 ? '#009018' : Number(project.progress) >= 51 ? '#BA7000' : '#B40003' }}>{Math.round(Number(project.progress))}%</Box>
          </TableCell>
        );
      case 'wwp':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', ...cellSx('wwp') }}>
            <Box sx={{ color: Number(project.wwp) >= 75 ? '#009018' : Number(project.wwp) >= 51 ? '#BA7000' : '#B40003' }}>{Math.round(Number(project.wwp))}%</Box>
          </TableCell>
        );
      case 'wwh':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', ...cellSx('wwh') }}>
            <Box sx={{ color: Number(project.wwh) >= 75 ? '#009018' : Number(project.wwh) >= 51 ? '#BA7000' : '#B40003' }}>{Math.round(Number(project.wwh))}%</Box>
          </TableCell>
        );
      case 'value':
        return (
          <TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('value') }}>
            {typeof project.value === 'number' ? Math.round(project.value / 1000) : (typeof project.value === 'string' ? Math.round(parseFloat(project.value) / 1000) : project.value)}
          </TableCell>
        );
      case 'sales':
        return (
          <TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('sales') }}>
            {typeof project.sales === 'number' ? Math.round(project.sales / 1000) : (typeof project.sales === 'string' ? Math.round(parseFloat(project.sales) / 1000) : project.sales)}
          </TableCell>
        );
      case 'pdz':
        return (
          <TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('pdz') }}>
            {typeof project.PDZ === 'number' ? Math.round(project.PDZ / 1000) : (typeof project.PDZ === 'string' ? Math.round(parseFloat(project.PDZ) / 1000) : 0)}
          </TableCell>
        );
      case 'budget':
        return (
          <TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('budget') }}>
            {typeof project.planBud === 'number' ? Math.round(project.planBud / 1000) : (typeof project.planBud === 'string' ? Math.round(parseFloat(project.planBud) / 1000) : project.planBud)}
          </TableCell>
        );
      case 'costs':
        return (
          <TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('costs') }}>
            {typeof project.costs === 'number' ? Math.round(project.costs / 1000) : (typeof project.costs === 'string' ? Math.round(parseFloat(project.costs) / 1000) : project.costs)}
          </TableCell>
        );
      case 'profit':
        return (<TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32', ...cellSx('profit') }}>{typeof project.profit === 'number' ? Math.round(project.profit / 1000) : project.profit}</TableCell>);
      case 'ros':
        return (<TableCell key={`c-${keyId}-${index}`} align="right" sx={{ ...cellStyle, ...cellSx('ros') }}>{project.ros}%</TableCell>);
      case 'drive':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, textAlign: 'center', px: 0.5, ...cellSx('drive'), verticalAlign: 'middle' }}>
            {project.google_drive_link ? (
              <IconButton
                size="small"
                component="a"
                href={project.google_drive_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ p: 0.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={(e)=>{ e.stopPropagation(); }}
                onMouseDown={(e)=>{ e.stopPropagation(); }}
                aria-label="Dysk Google"
              >
                <Box component="img" src="/assets/icons/drive.svg" alt="Dysk" />
              </IconButton>
            ) : '-'}
          </TableCell>
        );
      case 'camera':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, textAlign: 'center', px: 0.5, ...cellSx('camera'), verticalAlign: 'middle' }} data-no-row-nav="1" onClick={(e)=> e.stopPropagation()} onMouseDown={(e)=> e.stopPropagation()}>
            <IconButton
              size="small"
              sx={{ p: 0.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e)=>{ e.stopPropagation(); openCam(project); }}
              onMouseDown={(e)=>{ e.stopPropagation(); }}
              aria-label="Kamery"
            >
              <Box component="img" src="/assets/icons/camera.svg" alt="Kamery" />
            </IconButton>
          </TableCell>
        );
      case 'ruler':
        const hasBoard = !!(project as any).collectifile_board_id || !!(project as any).miro_link || (project as any).miroLink || (isUnitrescoUser && project.project_id && projectIdsWithBoard.has(project.project_id));
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, textAlign: 'center', px: 0.5, ...cellSx('ruler'), verticalAlign: 'middle' }}>
            {project.project_id ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                <IconButton
                  size="small"
                  component="a"
                  href={getBoardUrlForProject(project.project_id, project.project_name || project.name, (project as any).collectifile_board_id, (project as any).miro_link || (project as any).miroLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={(e)=>{ e.stopPropagation(); }}
                  onMouseDown={(e)=>{ e.stopPropagation(); }}
                  aria-label="Board"
                >
                  <RulerIcon filled={hasBoard} sx={{ width: 24, height: 24 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href={`/cad/${encodeURIComponent(project.project_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={(e)=>{ e.stopPropagation(); }}
                  onMouseDown={(e)=>{ e.stopPropagation(); }}
                  aria-label="CAD (DWG/DXF)"
                >
                  <Box component="img" src="/assets/icons/architecture.svg" alt="CAD" />
                </IconButton>
              </Box>
            ) : '-'}
          </TableCell>
        );
      case 'action':
        return (
          <TableCell key={`c-${keyId}-${index}`} sx={{ ...cellStyle, textAlign: 'center', px: 0.5, ...cellSx('action'), verticalAlign: 'middle' }}>
            <IconButton id={`action-button-${project.project_id}`} size="small" onClick={(event) => handleActionClick(event, project.id)} sx={{ p: 0.25 }}>
              <Box component="img" src="/assets/icons/more.svg" alt="Więcej" />
            </IconButton>
          </TableCell>
        );
      default:
        return null;
    }
  };

  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  const cellSx = React.useCallback((key: string) => ({ display: colV(key) ? 'table-cell' : 'none', width: getColWidthPx(key) }), [colV, columnWidths]);

  const materialsTotalValue = React.useMemo(() => {
    try {
      return materials.reduce((sum, m) => {
        const qty = Number(m.quantity || 0);
        const unitPrice = Number(m.unit_price || 0);
        const total = Number((m.value ?? (qty * unitPrice)) || 0);
        return sum + (Number.isFinite(total) ? total : 0);
      }, 0);
    } catch { return 0; }
  }, [materials]);

  const servicesTotalValue = React.useMemo(() => {
    try {
      return services.reduce((sum, sv) => {
        const qty = Number(sv.quantity || 0);
        const unitPrice = Number(sv.unit_price || 0);
        const total = Number((sv.value ?? (qty * unitPrice)) || 0);
        return sum + (Number.isFinite(total) ? total : 0);
      }, 0);
    } catch { return 0; }
  }, [services]);

  // Usunięto logikę sterowania dolnym panelem szczegółów

  const formatPLNCurrency = (v: number) => {
    try { return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 2 }).format(v); } catch { return String(Math.round(v)); }
  };

  // Sortowanie
  type SortKey = 'project' | 'kp' | 'start' | 'end' | 'days' | 'progress' | 'wwp' | 'wwh' | 'value' | 'sales' | 'pdz' | 'budget' | 'costs' | 'profit' | 'ros';
  const [orderBy, setOrderBy] = useState<SortKey>('project');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  // Mobile tab: 0 - W realizacji, 1 - Zakończone
  const [mobileTab, setMobileTab] = useState(0);

  // Inicjalizacja z localStorage
  useEffect(() => {
    try {
      const savedOrderBy = localStorage.getItem('projects.sort.orderBy') as SortKey | null;
      const savedOrder = localStorage.getItem('projects.sort.order') as 'asc' | 'desc' | null;
      const allowed: SortKey[] = ['project','kp','start','end','days','progress','wwp','wwh','value','sales','pdz','budget','costs','profit','ros'];
      if (savedOrderBy && allowed.includes(savedOrderBy)) {
        setOrderBy(savedOrderBy);
      }
      if (savedOrder === 'asc' || savedOrder === 'desc') {
        setOrder(savedOrder);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Zapis do localStorage
  useEffect(() => {
    try {
      localStorage.setItem('projects.sort.orderBy', orderBy);
      localStorage.setItem('projects.sort.order', order);
    } catch (e) {
      // ignore
    }
  }, [order, orderBy]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleManagerChange = (event: SelectChangeEvent) => {
    setManager(event.target.value as string);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = sortedProjects.map((project, index) => project.id + index);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string, isActionButton = false) => {
    if (isActionButton) {
      event.stopPropagation();
      return;
    }
    // Jeśli kliknięto w element oznaczony data-no-row-nav – nie nawiguj
    try {
      const el = event.target as HTMLElement;
      if (el && (el.closest('[data-no-row-nav="1"]'))) {
        event.stopPropagation();
        return;
      }
    } catch {}
    try {
      const rowEl = (event.currentTarget as HTMLElement);
      const projectId = rowEl?.getAttribute('data-project-id');
      if (projectId) {
        navigate(`/projects/${encodeURIComponent(projectId)}`);
        return;
      }
    } catch {}
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  // Podłączenie trybu śledzenia: otwarcie dolnego panelu po sygnale z lekkiego follow
  useEffect(() => {
    const onExternalSelect = (e: any) => {
      const pid = e?.detail?.projectId;
      if (!pid) return;
      // znajdź pierwszy index pasujący do projektu i zaznacz go – wykorzystujemy istniejący handleClick
      const idx = sortedProjects.findIndex((p, i) => String((p as any).project_id || p.id).startsWith(String(pid)));
      if (idx >= 0) {
        try { handleClick({ stopPropagation(){} } as any, sortedProjects[idx].id + idx); } catch {}
        // przewiń do kotwicy dolnego panelu, jeśli istnieje
        try {
          const anchor = document.querySelector('[data-project-details-anchor], [data-testid="project-details-anchor"]') as HTMLElement | null;
          anchor?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        } catch {}
      }
    };
    window.addEventListener('ui:projects:select:external', onExternalSelect as any);
    return () => window.removeEventListener('ui:projects:select:external', onExternalSelect as any);
  }, [/* eslint-disable-line */,]);

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    event.stopPropagation();
    setActionProjectId(projectId);
    if (isMobile) {
      const found = projectsData.find(p => p.id === projectId) || null;
      const raw = (found?.project_id || (found?.id || '')) as string;
      const actualId = (typeof raw === 'string' && raw.includes(' ')) ? raw.split(' ')[0] : raw;
      navigate(`/projects/${encodeURIComponent(actualId)}`);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  // Pobierz materiały po wejściu do karty materiałów
  useEffect(() => {
    const loadMaterials = async () => {
      try {
        if (sheetView !== 'materials' || !selectedProjectMobile) return;
        const projectId = (selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string;
        const res = await (await import('../services/materialService')).default.getMaterialsByProject(projectId);
        setMaterials(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error('Błąd pobierania materiałów', e);
        setMaterials([]);
      }
    };
    loadMaterials();
  }, [sheetView, selectedProjectMobile]);

  // Pobierz usługi po wejściu do karty usług
  useEffect(() => {
    const loadServices = async () => {
      try {
        if (sheetView !== 'services' || !selectedProjectMobile) return;
        const projectId = (selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string;
        const res = await serviceService.getServicesByProject(projectId);
        setServices(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error('Błąd pobierania usług', e);
        setServices([]);
      }
    };
    loadServices();
  }, [sheetView, selectedProjectMobile]);

  // Load subcontractors when opening subcontractors sheet
  useEffect(() => {
    const loadSubs = async () => {
      try {
        if (sheetView !== 'subcontractors' || !selectedProjectMobile) return;
        setSubsLoading(true);
        const projectId = (selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string;
        const list = await subcontractorService.getSubcontractorsByProjectId(projectId);
        setSubs(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Błąd pobierania podwykonawców', e);
        setSubs([]);
      } finally {
        setSubsLoading(false);
      }
    };
    loadSubs();
  }, [sheetView, selectedProjectMobile]);

  const handleActionClose = () => {
    setAnchorEl(null);
  };

  const handleActionSelect = (action: string) => {
    const project = projectsData.find(p => p.id === actionProjectId);
    if (project) {
      if (action === 'edit') {
        // Otwórz formularz edycji projektu
        setSelectedProject(project);
        setEditMode(true);
        setOpenAddForm(true);
      } else if (action === 'drive' && project.driveLink) {
        window.open(project.driveLink, '_blank');
      } else if (action === 'board') {
        const id = project.project_id;
        const name = project.project_name || project.name;
        const manualLink = (project as any).miro_link || (project as any).miroLink;
        if (id) window.open(getBoardUrlForProject(id, name, (project as any).collectifile_board_id, manualLink), '_blank');
      } else if (action === 'dwg') {
        const id = project.project_id;
        if (id) window.open(`/cad/${encodeURIComponent(id)}`, '_blank');
      } else if (action === 'phase-change' && project.folderId) {
        // Otwórz menu zmiany fazy projektu
        const element = document.getElementById(`action-button-${project.project_id}`);
        if (element) {
          setPhaseChangeProjectId(project.project_id);
          setPhaseMenuAnchorEl(element);
        }
      }
    }
    handleActionClose();
  };

  const handlePhaseMenuClose = () => {
    setPhaseMenuAnchorEl(null);
  };

  const handlePhaseChange = async (newPhase: 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI') => {
    try {
      setLoading(true);
      const project = projectsData.find(p => p.project_id === phaseChangeProjectId);
      
      if (project && project.folderId) {
        // Aktualizuj fazę projektu i przenieś folder
        const response = await projectService.updateProjectPhase(
          project.project_id,
          newPhase,
          project.folderId,
          '0AFavSTLoT0DKUk9PVA' // ID dysku współdzielonego "5_PROJEKTY"
        );
        
        if (response.data) {
          const updatedProject = response.data;
          
          // Aktualizuj dane projektu w stanie
          const updatedProjects = projectsData.map(p => 
            p.project_id === updatedProject.project_id ? 
              convertProjectToDisplayData(updatedProject) : p
          );
          
          setProjectsData(updatedProjects);
        }
        
        // Sprawdź, czy wystąpił błąd podczas przenoszenia folderu
        if (response.warning) {
          // Wyświetl powiadomienie o częściowym sukcesie
          const message = `Projekt ${project.project_name} został przeniesiony do fazy "${newPhase}", ale nie udało się przenieść folderu na Google Drive. Szczegóły: ${response.warning}`;
          showNotification(message, 'info');
          
          // Wyświetl alert w konsoli dla debugowania
          console.log('INFO:', message);
        } else {
          // Wyświetl powiadomienie o pełnym sukcesie
          const message = `Projekt ${project.project_name} został przeniesiony do fazy "${newPhase}"`;
          showNotification(message, 'success');
          
          // Wyświetl alert w konsoli dla debugowania
          console.log('SUCCESS:', message);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas zmiany fazy projektu');
      console.error('Błąd podczas zmiany fazy projektu:', err);
      
      // Wyświetl powiadomienie o błędzie
      const errorMessage = `Błąd: ${err.message || 'Wystąpił błąd podczas zmiany fazy projektu'}`;
      showNotification(errorMessage, 'error');
      
      // Wyświetl alert w konsoli dla debugowania
      console.log('ERROR:', errorMessage);
    } finally {
      setLoading(false);
      handlePhaseMenuClose();
    }
  };
  

  const handleUpdateFinancialData = async () => {
    try {
      setLoading(true);
      showNotification('Pobieranie danych finansowych z MySQL...', 'info');
      
      // Wywołanie metody do pobierania danych finansowych wszystkich projektów z MySQL
      const result = await projectService.getAllProjectsFinancialData();
      
      // Pobierz zaktualizowane projekty
      const updatedProjects = await projectService.getAllProjects();
      
      // Aktualizacja projektów o dane finansowe z MySQL
      const projectsWithFinancialData = updatedProjects.map(project => {
        // Znajdź dane finansowe dla tego projektu
        const projectFinancialData = result.projects.find(
          p => p.project_id === project.project_id
        );
        
        if (projectFinancialData) {
          // Aktualizuj projekt o dane finansowe
          return {
            ...project,
            sales: projectFinancialData.sprzedaz,
            costs: projectFinancialData.koszty,
            PDZ: projectFinancialData.pdz
          };
        }
        
        return project;
      });
      
      // Konwersja projektów na format wyświetlany w tabeli
      const displayProjects = projectsWithFinancialData.map(project => convertProjectToDisplayData(project));
      
      // Aktualizacja stanu
      setProjectsData(displayProjects);
      
      // Wyświetl powiadomienie o sukcesie
      const successMessage = `Pobrano dane finansowe dla ${result.projects.length} projektów z MySQL. ${result.errors.length > 0 ? `Błędy: ${result.errors.length}` : ''}`;
      showNotification(successMessage, 'success');
      
      console.log('Pobrane dane projektów z MySQL:', result.projects);
      if (result.errors.length > 0) {
        console.error('Błędy podczas pobierania danych projektów:', result.errors);
      }
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas aktualizacji danych finansowych');
      console.error('Błąd podczas aktualizacji danych finansowych:', err);
      
      // Wyświetl powiadomienie o błędzie
      const errorMessage = `Błąd: ${err.message || 'Wystąpił błąd podczas aktualizacji danych finansowych'}`;
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenImportDialog = () => {
    setOpenImportDialog(true);
  };

  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
  };

  const handleImportProjects = (importedProjects: Project[]) => {
    // Konwersja zaimportowanych projektów na format wyświetlany w tabeli
    const displayProjects = importedProjects.map(project => convertProjectToDisplayData(project));
    
    // Dodaj nowe projekty do listy
    setProjectsData(prevProjects => {
      // Filtruj, aby uniknąć duplikatów
      const existingIds = new Set(prevProjects.map(p => p.project_id));
      const newProjects = displayProjects.filter(p => !existingIds.has(p.project_id));
      
      return [...prevProjects, ...newProjects];
    });
  };

  // Obsługa legacy deeplinków: przekieruj do nowej ścieżki szczegółów
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const openWhat = params.get('open');
      const projectId = params.get('projectId');
      const subId = params.get('subId');
      if (projectId && (openWhat === 'subcontractor' || openWhat === 'packages')) {
        try {
          const extra =
            openWhat === 'packages'
              ? `?open=packages`
              : (subId ? `?subId=${encodeURIComponent(subId)}` : '');
          window.location.replace(`/projects/${encodeURIComponent(projectId)}${extra}`);
        } catch {}
      }
    } catch {}
  }, []);

  // Pobieranie danych projektów z API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Pobieranie projektów z API
        const projects = await projectService.getAllProjects();
        
        // Pobieranie danych finansowych z MySQL (bez psucia listy, jeśli się nie uda)
        let financialData: { projects: Array<{ project_id: string; sprzedaz: number; koszty: number; pdz: number; }>, errors: any[] } | null = null;
        try {
          financialData = await projectService.getAllProjectsFinancialData();
        } catch (e) {
          console.warn('Nie udało się pobrać danych finansowych – wyświetlam projekty bez agregatów.', e);
        }
        
        // Aktualizacja projektów o dane finansowe z MySQL
        const updatedProjects = projects.map(project => {
          // Znajdź dane finansowe dla tego projektu
          const projectFinancialData = financialData?.projects?.find(p => p.project_id === project.project_id);
          
          if (projectFinancialData) {
            // Aktualizuj projekt o dane finansowe
            return {
              ...project,
              sales: projectFinancialData.sprzedaz,
              costs: projectFinancialData.koszty,
              PDZ: projectFinancialData.pdz
            };
          }
          
          return project;
        });
        
        // Konwersja projektów na format wyświetlany w tabeli
        const displayProjects = updatedProjects.map(project => convertProjectToDisplayData(project));
        
        setProjectsData(displayProjects);
        if (financialData) console.log('Pobrano dane finansowe z MySQL:', financialData);
      } catch (err: any) {
        setError(err.message || 'Wystąpił błąd podczas pobierania projektów');
        console.error('Błąd podczas pobierania projektów:', err);
        console.error('Szczegóły błędu:', err.response?.data || err.message);
        
        // Nie zeruj listy – zostaw poprzedni stan aby UI nie był puste
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);

  // Usunięto wcześniejszą próbę zaznaczenia przed sortowaniem

  

  const handleOpenAddForm = () => {
    setEditMode(false);
    setSelectedProject(null);
    setOpenAddForm(true);
  };

  const handleOpenEditForm = () => {
    if (selected.length === 1) {
      // Znajdź indeks zaznaczonego projektu
      let selectedIndex = -1;
      for (let i = 0; i < sortedProjects.length; i++) {
        if (isSelected(sortedProjects[i].id + i)) {
          selectedIndex = i;
          break;
        }
      }
      
      if (selectedIndex !== -1) {
        const projectToEdit = sortedProjects[selectedIndex];
        setSelectedProject(projectToEdit);
        setEditMode(true);
        setOpenAddForm(true);
      }
    }
  };

  const handleCloseAddForm = () => {
    setOpenAddForm(false);
    setEditMode(false);
    setSelectedProject(null);
  };

  const handleAddProject = (newProject: Project) => {
    // Konwersja nowego projektu na format wyświetlany w tabeli
    const displayProject = convertProjectToDisplayData(newProject);
    setProjectsData([...projectsData, displayProject]);
  };

  const handleEditProject = (editedProject: Project) => {
    // Konwersja edytowanego projektu na format wyświetlany w tabeli
    const displayProject = convertProjectToDisplayData(editedProject);
    const updatedProjects = projectsData.map((project) => 
      project.project_id === editedProject.project_id ? displayProject : project
    );
    setProjectsData(updatedProjects);
    setSelected([]);
  };

  const handleDeleteProject = (projectId: string) => {
    const updatedProjects = projectsData.filter((project) => project.project_id !== projectId);
    setProjectsData(updatedProjects);
    setSelected([]);
  };

// Filtrowanie projektów według fazy projektu
const projectsByPhase = {
  'W REALIZACJI': projectsData.filter(project => 
    project.project_phase === 'W REALIZACJI' || !project.project_phase
  ),
  'ZAKOŃCZONE W OKRESIE GWARANCJI': projectsData.filter(project => 
    project.project_phase === 'ZAKOŃCZONE W OKRESIE GWARANCJI'
  ),
  'ZAKOŃCZONE PO GWARANCJI': projectsData.filter(project => 
    project.project_phase === 'ZAKOŃCZONE PO GWARANCJI'
  )
};

// Funkcja sprawdzająca czy projekt spełnia aktywne filtry (wspólna dla tabeli i liczników)
const matchesFilters = React.useCallback((project: ProjectDisplayData) => {
  const matchesSearch = searchText === '' || project.id.toLowerCase().includes(searchText.toLowerCase());
  const matchesManager = manager === '' || project.manager === manager;
  const matchesStartDate = startDate === '' || new Date(project.startDate) >= new Date(startDate);
  const matchesEndDate = endDate === '' || new Date(project.endDate) <= new Date(endDate);
  return matchesSearch && matchesManager && matchesStartDate && matchesEndDate;
}, [searchText, manager, startDate, endDate]);

// Liczniki w zakładkach po zastosowaniu filtrów
const tabCounts = React.useMemo(() => ({
  'W REALIZACJI': projectsByPhase['W REALIZACJI'].filter(matchesFilters).length,
  'ZAKOŃCZONE W OKRESIE GWARANCJI': projectsByPhase['ZAKOŃCZONE W OKRESIE GWARANCJI'].filter(matchesFilters).length,
  'ZAKOŃCZONE PO GWARANCJI': projectsByPhase['ZAKOŃCZONE PO GWARANCJI'].filter(matchesFilters).length,
}), [projectsByPhase['W REALIZACJI'], projectsByPhase['ZAKOŃCZONE W OKRESIE GWARANCJI'], projectsByPhase['ZAKOŃCZONE PO GWARANCJI'], matchesFilters]);

  // Wybór projektów dla aktualnej zakładki
  let currentPhaseProjects = tabValue === 0 
    ? projectsByPhase['W REALIZACJI'] 
    : tabValue === 1 
      ? projectsByPhase['ZAKOŃCZONE W OKRESIE GWARANCJI'] 
      : projectsByPhase['ZAKOŃCZONE PO GWARANCJI'];
  
  // Sortowanie projektów od największego do najmniejszego ID
  currentPhaseProjects = [...currentPhaseProjects].sort((a, b) => {
    // Wyciągnij numery projektów z ID (np. "216-217" -> 216)
    const aNum = parseInt(a.project_id.split('-')[0]);
    const bNum = parseInt(b.project_id.split('-')[0]);
    return bNum - aNum; // Sortowanie malejąco
  });

  // Filtrowanie projektów
  const filteredProjects = currentPhaseProjects.filter(project => {
    // Filtrowanie po tekście wyszukiwania
    const matchesSearch = searchText === '' || 
      project.id.toLowerCase().includes(searchText.toLowerCase());
    
    // Filtrowanie po kierowniku projektu
    const matchesManager = manager === '' || project.manager === manager;
    
    // Filtrowanie po dacie początkowej
    const matchesStartDate = startDate === '' || 
      new Date(project.startDate) >= new Date(startDate);
    
    // Filtrowanie po dacie końcowej
    const matchesEndDate = endDate === '' || 
      new Date(project.endDate) <= new Date(endDate);
    
    return matchesSearch && matchesManager && matchesStartDate && matchesEndDate;
  });

  // Posortowane projekty
  const sortedProjects = React.useMemo(() => {
    const getNumberFrom = (value: any): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? Number.NaN : parsed;
      }
      return Number.NaN;
    };

    const compare = (a: ProjectDisplayData, b: ProjectDisplayData) => {
      let va: any;
      let vb: any;
      switch (orderBy) {
        case 'project':
          // Sortuj po numerze projektu (prefiks przed myślnikiem)
          va = parseInt(a.project_id.split('-')[0]);
          vb = parseInt(b.project_id.split('-')[0]);
          break;
        case 'kp':
          va = (a.manager || a.responsible_person2 || '').toString().toLowerCase();
          vb = (b.manager || b.responsible_person2 || '').toString().toLowerCase();
          break;
        case 'start':
          va = a.startDate ? new Date(a.startDate).getTime() : Number.NaN;
          vb = b.startDate ? new Date(b.startDate).getTime() : Number.NaN;
          break;
        case 'end':
          va = a.endDate ? new Date(a.endDate).getTime() : Number.NaN;
          vb = b.endDate ? new Date(b.endDate).getTime() : Number.NaN;
          break;
        case 'days':
          va = getNumberFrom(a.days);
          vb = getNumberFrom(b.days);
          break;
        case 'progress':
          va = getNumberFrom(a.progress);
          vb = getNumberFrom(b.progress);
          break;
        case 'wwp':
          va = getNumberFrom(a.wwp);
          vb = getNumberFrom(b.wwp);
          break;
        case 'wwh':
          va = getNumberFrom(a.wwh);
          vb = getNumberFrom(b.wwh);
          break;
        case 'value':
          va = getNumberFrom(a.value);
          vb = getNumberFrom(b.value);
          break;
        case 'sales':
          va = getNumberFrom(a.sales);
          vb = getNumberFrom(b.sales);
          break;
        case 'pdz':
          va = getNumberFrom(a.PDZ);
          vb = getNumberFrom(b.PDZ);
          break;
        case 'budget':
          va = getNumberFrom(a.planBud);
          vb = getNumberFrom(b.planBud);
          break;
        case 'costs':
          va = getNumberFrom(a.costs);
          vb = getNumberFrom(b.costs);
          break;
        case 'profit':
          va = getNumberFrom(a.profit);
          vb = getNumberFrom(b.profit);
          break;
        case 'ros':
          va = getNumberFrom(a.ros);
          vb = getNumberFrom(b.ros);
          break;
        default:
          va = 0; vb = 0;
      }

      const aNaN = typeof va === 'number' && Number.isNaN(va);
      const bNaN = typeof vb === 'number' && Number.isNaN(vb);
      if (aNaN && bNaN) return 0;
      if (aNaN) return order === 'asc' ? 1 : -1;
      if (bNaN) return order === 'asc' ? -1 : 1;

      if (typeof va === 'number' && typeof vb === 'number') {
        return order === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      if (sa < sb) return order === 'asc' ? -1 : 1;
      if (sa > sb) return order === 'asc' ? 1 : -1;
      return 0;
    };

    return [...filteredProjects].sort(compare);
  }, [filteredProjects, order, orderBy]);

  // Legacy deeplink: /projects?open=select&projectId=XXX -> przekieruj na /projects/:id
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const openWhat = params.get('open');
      const projectId = params.get('projectId');
      if (openWhat === 'select' && projectId) {
        navigate(`/projects/${encodeURIComponent(projectId)}`, { replace: true });
      }
    } catch {}
  }, [navigate]);

  // Obsługa parametru tab w URL: /projects?tab=0/1/2
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam !== null) {
        const tabNum = parseInt(tabParam, 10);
        if (!isNaN(tabNum) && tabNum >= 0 && tabNum <= 2) {
          setTabValue(tabNum);
        }
      }
    } catch {}
  }, []);

  const handleRequestSort = (property: SortKey) => {
    if (orderBy === property) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      // Domyślnie rosnąco poza numerem projektu (który naturalnie chcemy malejąco)
      setOrder(property === 'project' ? 'desc' : 'asc');
    }
  };

  // Obliczanie sum dla przefiltrowanych projektów
  const totalValue = filteredProjects.reduce((sum, project) => {
    const value = typeof project.value === 'number' ? project.value : 0;
    return sum + value;
  }, 0);
  
  const totalSales = filteredProjects.reduce((sum, project) => {
    const sales = typeof project.sales === 'number' ? project.sales : 0;
    return sum + sales;
  }, 0);
  
  const totalPozDz = filteredProjects.reduce((sum, project) => {
    const pozDz = typeof project.pozDz === 'number' ? project.pozDz : 0;
    return sum + pozDz;
  }, 0);
  
  const totalPlanBud = filteredProjects.reduce((sum, project) => {
    const planBud = typeof project.planBud === 'number' ? project.planBud : 0;
    return sum + planBud;
  }, 0);
  
  const totalCosts = filteredProjects.reduce((sum, project) => {
    const costs = typeof project.costs === 'number' ? project.costs : 0;
    return sum + costs;
  }, 0);
  
  const totalProfit = filteredProjects.reduce((sum, project) => {
    const profit = typeof project.profit === 'number' ? project.profit : 0;
    return sum + profit;
  }, 0);

  // Style dla komórek tabeli
  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '150px',
    padding: '6px 12px', // większy padding jak w Figmie
    fontSize: '0.875rem'
  };

  // Widok mobilny – lista w realizacji: Projekt | Koszty | 3 kropki
  if (isMobile) {
    const activeList = [...projectsByPhase['W REALIZACJI']];
    const closedList = [
      ...projectsByPhase['ZAKOŃCZONE W OKRESIE GWARANCJI'],
      ...projectsByPhase['ZAKOŃCZONE PO GWARANCJI']
    ];
    const source = mobileTab === 0 ? activeList : closedList;
    const mobileProjects = source
      .filter(p => searchText === '' || p.id.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => parseInt(b.project_id.split('-')[0]) - parseInt(a.project_id.split('-')[0]));

    const formatK = (v: any) => {
      const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : 0;
      const k = Math.round(n / 1000);
      try { return `${new Intl.NumberFormat('pl-PL').format(k)} k`; } catch { return `${k} k`; }
    };

    return (
      <Box>
        {/* Arkusz dolny (bottom sheet) dla akcji projektu */}
        <Drawer 
          anchor="bottom" 
          open={mobileSheetOpen} 
          onClose={() => { setMobileSheetOpen(false); }}
          PaperProps={{ sx: { height: SHEET_HEIGHT, borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}
        >
          <Box sx={{ display:'flex', flexDirection:'column', height: '100%' }}>
            {/* Pasek nagłówka jak w Google Drive */}
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', px:2, py:1, borderBottom: '1px solid #eee' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign:'center' }}>
                {selectedProjectMobile?.id || 'Projekt'}
              </Typography>
            </Box>
            {/* Zawartość przewijana */}
            <Box sx={{ p: 1, flex: 1, overflowY: 'auto' }}>
            {sheetView === 'main' && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-around', px:2, pb:1 }}>
                  <Button
                    onClick={() => { const link = selectedProjectMobile?.driveLink; if (link) window.open(link, '_blank'); }}
                    disabled={!selectedProjectMobile?.driveLink}
                    startIcon={<Box component="img" src={DriveLogo} alt="Dysk" sx={{ width: 33, height: 33 }} />}
                    sx={{ textTransform: 'none', color: 'text.primary' }}
                  >
                    Dysk
                  </Button>
                </Box>
                <Divider />
                <List>
                  <ListItem button onClick={() => setSheetView('details')}>
                    <ListItemText primary="Szczegóły" />
                  </ListItem>
                  <ListItem button onClick={() => setSheetView('contracting')} secondaryAction={<ChevronRight fontSize="small" /> }>
                    <ListItemText primary="Kontraktowanie" />
                  </ListItem>
                  <ListItem button onClick={()=> setSheetView('orderplan')}>
                    <ListItemText primary="Plan zamówień" />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem button onClick={()=> setSheetView('schedule')}>
                    <ListItemText primary="Harmonogram" />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem button onClick={()=>{ setMobileSheetOpen(false); showNotification('KZM — w przygotowaniu', 'info'); }}>
                    <ListItemText primary="KZM" />
                  </ListItem>
                  <ListItem button onClick={()=>{ setMobileSheetOpen(false); showNotification('Przepływy pieniężne — w przygotowaniu', 'info'); }}>
                    <ListItemText primary="Przepływy pieniężne" />
                  </ListItem>
                  <ListItem button onClick={() => setSheetView('materials')}>
                    <ListItemText primary="Materiały" />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem button onClick={()=> setSheetView('services')}>
                    <ListItemText primary="Usługi" />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem button onClick={()=> setSheetView('subcontractors')}>
                    <ListItemText primary="Podwykonawcy" />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                </List>
              </>
            )}
            {sheetView === 'contracting' && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Kontraktowanie</Typography>
                </Box>
                <Divider />
                <List>
                  <ListItem button onClick={()=>setSheetView('packages')}>
                    <ListItemText primary={`PAKIETY (${pkgCount})`} />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem button onClick={()=>setSheetView('inquiries')}>
                    <ListItemText primary={`ZAPYTANIA (${inqCount})`} />
                    <ChevronRight fontSize="small" />
                  </ListItem>
                  <ListItem><ListItemText primary={`ZESTAWIENIE OFERT (${offCount})`} /></ListItem>
                  <ListItem><ListItemText primary={`ZAMÓWIENIA (${ordCount})`} /></ListItem>
                  <ListItem><ListItemText primary={`DRAFTY UMÓW Z PW (${draftCount})`} /></ListItem>
                </List>
              </>
            )}
            {sheetView === 'packages' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('contracting')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pakiety</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <MobilePackagesList
                    projectId={(selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string}
                    projectName={selectedProjectMobile.name}
                  />
                </Box>
              </>
            )}
            {sheetView === 'inquiries' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('contracting')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zapytania</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <MobileInquiriesList
                    projectId={(selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string}
                    projectName={selectedProjectMobile.name}
                  />
                </Box>
              </>
            )}
            {sheetView === 'details' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Szczegóły</Typography>
                </Box>
                <Divider />
                <Box sx={{ px:2, py:1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Projekt</Typography>
                  <Typography variant="body1" sx={{ mb:1 }}>{selectedProjectMobile.id}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Kierownik projektu</Typography>
                  <Typography variant="body1" sx={{ mb:1 }}>{selectedProjectMobile.responsible_person2 || '—'}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Daty</Typography>
                  <Typography variant="body1" sx={{ mb:1 }}>{selectedProjectMobile.startDate || '—'} — {selectedProjectMobile.endDate || '—'}</Typography>
                  <Divider sx={{ my:1 }} />
                  <Typography variant="subtitle2" color="text.secondary">Finanse</Typography>
                  <Typography variant="body2">Sprzedaż: {Math.round((Number(selectedProjectMobile.sales)||0)/1000)} k</Typography>
                  <Typography variant="body2">Koszty: {Math.round((Number(selectedProjectMobile.costs)||0)/1000)} k</Typography>
                  <Typography variant="body2">EBIDTA: {Math.round((Number(selectedProjectMobile.profit)||0)/1000)} k</Typography>
                </Box>
              </>
            )}
            {sheetView === 'orderplan' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Plan zamówień</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 1, py: 1 }}>
                  <MobileOrderPlanList projectId={(selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string} />
                </Box>
              </>
            )}
            {sheetView === 'materials' && selectedProjectMobile && (
              <>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ position:'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                      <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                      <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>Materiały</Typography>
                      <Box sx={{ bgcolor: '#e3f2fd', color: '#1565c0', borderRadius: 9999, px: 1.5, py: 0.5, fontSize: '0.95rem', whiteSpace: 'nowrap', mr: 1 }}>
                        Suma: {formatPLNCurrency(materialsTotalValue)}
                      </Box>
                      {selectedMaterials.length > 0 && (
                        <Button variant="contained" size="small" onClick={()=>{
                          const lines = selectedMaterials.map((m:any, i:number)=> `${i+1}. ${m.symbol} (${m.unit || 'szt.'})`).join('\n');
                          setOrderContractor(selectedMaterials[0]?.contractor_email || '');
                          setOrderContent(lines);
                          setOrderFormOpen(true);
                        }}>Zamów</Button>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ p:1 }}>
                      <TextField fullWidth size="small" placeholder="Szukaj materiału" value={materialsSearch} onChange={(e)=>setMaterialsSearch(e.target.value)} sx={{ mb:1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <List>
                      {materials.filter(m => {
                        const name = (m.client || m.client_name || m.contractor_name || m.contractor || m.vendor || m.supplier || '').toString();
                        return `${m.symbol || ''} ${name}`.toLowerCase().includes(materialsSearch.toLowerCase());
                      }).map((m, idx) => {
                        const clientName = (m.client || m.client_name || m.contractor_name || m.contractor || m.vendor || m.supplier || '-') as string;
                        const qty = Number(m.quantity || 0);
                        const unitPrice = Number(m.unit_price || 0);
                        const total = m.value ?? (qty * unitPrice);
                        return (
                        <ListItem key={idx} secondaryAction={
                          <Checkbox edge="end" checked={selectedMaterials.includes(m)} onChange={()=>{
                            setSelectedMaterials((prev)=> prev.includes(m) ? prev.filter(p=>p!==m) : [...prev, m]);
                          }} />
                        }>
                          <ListItemText 
                            primary={`${m.symbol || '—'}`}
                            secondary={`${clientName || '—'} • Ilość: ${qty} • Cena jedn.: ${unitPrice} • Wartość: ${total}`}
                          />
                        </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                  {/* Przycisk Zamów przeniesiony do nagłówka */}
                </Box>
              </>
            )}
            {sheetView === 'services' && selectedProjectMobile && (
              <>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ position:'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                      <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                      <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>Usługi</Typography>
                      <Box sx={{ bgcolor: '#e3f2fd', color: '#1565c0', borderRadius: 9999, px: 1.5, py: 0.5, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                        Suma: {formatPLNCurrency(servicesTotalValue)}
                      </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ p:1 }}>
                      <TextField fullWidth size="small" placeholder="Szukaj usługi" value={servicesSearch} onChange={(e)=>setServicesSearch(e.target.value)} sx={{ mb:1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <List>
                      {services.filter(sv => {
                        const name = (sv.client || sv.client_name || sv.contractor_name || sv.contractor || sv.vendor || sv.supplier || '').toString();
                        return `${sv.symbol || ''} ${name}`.toLowerCase().includes(servicesSearch.toLowerCase());
                      }).map((sv, idx) => {
                        const clientName = (sv.client || sv.client_name || sv.contractor_name || sv.contractor || sv.vendor || sv.supplier || '-') as string;
                        const qty = Number(sv.quantity || 0);
                        const unitPrice = Number(sv.unit_price || 0);
                        const total = sv.value ?? (qty * unitPrice);
                        return (
                        <ListItem key={idx}>
                          <ListItemText 
                            primary={`${sv.symbol || '—'}`}
                            secondary={`${clientName || '—'} • Ilość: ${qty} • Cena jedn.: ${unitPrice} • Wartość: ${total}`}
                          />
                        </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                </Box>
              </>
            )}
            {sheetView === 'schedule' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Harmonogram</Typography>
                  <Box sx={{ bgcolor: '#fce4ec', color: '#ad1457', borderRadius: 9999, px: 1.5, py: 0.5, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    Dzisiaj: {new Date().toISOString().slice(0,10)}
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ px: 0, py: 0 }}>
                  <MobileScheduleTab projectId={(selectedProjectMobile.project_id || (selectedProjectMobile.id?.split(' ')[0] || '')) as string} />
                </Box>
              </>
            )}
            {sheetView === 'subcontractors' && selectedProjectMobile && (
              <>
                <Box sx={{ display:'flex', alignItems:'center', px:1, pb:1 }}>
                  <IconButton onClick={()=>setSheetView('main')}><ArrowBack /></IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Podwykonawcy</Typography>
                </Box>
                <Divider />
                <List>
                  {subsLoading ? (
                    <ListItem><ListItemText primary="Ładowanie..." /></ListItem>
                  ) : subs.length === 0 ? (
                    <ListItem><ListItemText primary="Brak podwykonawców" /></ListItem>
                  ) : (
                    subs.map((s) => {
                      const shortName = (s.name || '').slice(0, 20);
                      return (
                        <ListItem
                          key={s.id}
                          secondaryAction={
                            <>
                              <IconButton size="small" onClick={(e)=>{ setSelectedSub(s); setSubMenuAnchor(e.currentTarget); }}>
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </>
                          }
                        >
                          <ListItemText
                            primary={`${s.contract_number || '—'} • ${shortName}`}
                            secondary={`Zaaw.: ${Math.round(Number(s.progress||0))}% • Wartość: ${new Intl.NumberFormat('pl-PL',{style:'currency',currency:'PLN',maximumFractionDigits:0}).format(Number(s.contract_value||0))}`}
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
                {/* Menu akcji podwykonawcy */}
                <Menu anchorEl={subMenuAnchor} open={Boolean(subMenuAnchor)} onClose={()=>setSubMenuAnchor(null)}>
                  <MenuItem onClick={()=>{ setSubMenuAnchor(null); setPocrOpen(true); }}>POCR</MenuItem>
                  <MenuItem onClick={()=>{ setSubMenuAnchor(null); setPokrOpen(true); }}>POKR</MenuItem>
                  <MenuItem onClick={()=>{ setSubMenuAnchor(null); showNotification('Wezwanie – w przygotowaniu','info'); }}>Wezwanie</MenuItem>
                  <MenuItem onClick={()=>{ setSubMenuAnchor(null); setSubDetailsOpen(true); }}>Szczegóły</MenuItem>
                </Menu>
              </>
            )}
          </Box>
          </Box>
        </Drawer>
      {/* Panel kamery – ultra-prosty overlay fixed (wysoki z-index) */}
      {cameraOpen && (
        <Box sx={{ position: 'fixed', inset: { xs: '56px 0 0 auto', sm: '52px 0 0 auto' }, right: 0, width: { xs: '100%', md: '50vw' }, height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 52px)' }, bgcolor: '#fff', zIndex: 999999, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, p: 2, overflow: 'auto', border: '1px solid #D4D4D9', display: 'flex', flexDirection: 'column' }}>
          <IconButton onClick={()=>setCameraOpen(false)} aria-label="Zamknij" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, width: 24, height: 24 }}>
            <Box component="img" src="/assets/icons/close.svg" alt="Zamknij" sx={{ width: 13, height: 13 }} />
          </IconButton>
          {cameraProject && cameraHardwareLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0, pr: 4 }}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Ładowanie urządzeń…</Typography></Box>
          )}
          {cameraProject && !cameraHardwareLoading && cameraHardwareDevices.length === 0 && (
            <Alert severity="info" sx={{ mb: 1, py: 0.5, flexShrink: 0, '& .MuiAlert-message': { py: 0 } }}>Brak urządzenia w Ustawieniach → Hardware dla tego projektu. Używane są domyślne adresy magazynowe.</Alert>
          )}
          {cameraProject && !cameraHardwareLoading && cameraHardwareDevices.length > 0 && !hasHardwareCameras && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.5, flexShrink: 0, '& .MuiAlert-message': { py: 0 } }}>Ustaw „Liczba kamer” większą od zera przy urządzeniu w Ustawieniach → Hardware.</Alert>
          )}
          {/* Ramki wyboru trybu i daty */}
          <Box sx={{ width: '100%', mb: 1, display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
              <Box
                onClick={(e)=> setModeMenuAnchor(e.currentTarget)}
                sx={{
                  width: 300,
                  height: 36,
                  border: '1px solid #D4D4D9',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  bgcolor: '#fff',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#595966' }
                }}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{camMode === 'live' ? 'Na żywo' : 'Odtwarzaj'}</Typography>
                <Box component="img" src="/assets/icons/chevron-down-small.svg" alt="▼" sx={{ width: 9, height: 5, ml: 1 }} />
              </Box>
              <TextField
                size="small"
                type="date"
                placeholder="dd.mm.rrrr"
                value={camDate}
                inputRef={camDateRef}
                onChange={(e)=> setCamDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="img" src="/assets/icons/calendar-small.svg" alt="Kal" sx={{ width: 13, height: 15, cursor: camMode==='live' ? 'default' : 'pointer', opacity: camMode==='live' ? 0.5 : 1 }} onClick={()=> camMode==='live' ? null : (camDateRef.current?.showPicker?.() || camDateRef.current?.focus())} />
                    </InputAdornment>
                  ),
                  endAdornment: null
                }}
                sx={{
                  width: 200,
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4D4D9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiInputBase-input': { color: camMode==='live' ? '#9e9e9e' : '#2D2D32' },
                  '& input[type="date"]::-webkit-calendar-picker-indicator': { display: 'none' },
                  '& input[type="date"]::-webkit-inner-spin-button': { display: 'none' },
                  '& input[type="date"]::-webkit-clear-button': { display: 'none' }
                }}
                disabled={camMode==='live'}
              />
              <Menu anchorEl={modeMenuAnchor} open={Boolean(modeMenuAnchor)} onClose={()=>setModeMenuAnchor(null)}>
                <MenuItem onClick={()=>{ setCamMode('live'); setModeMenuAnchor(null); }}>Na żywo</MenuItem>
                <MenuItem onClick={()=>{ setCamMode('playback'); setModeMenuAnchor(null); }}>Odtwarzaj</MenuItem>
              </Menu>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `repeat(${cameraGridCols}, 1fr)`, gridAutoRows: '1fr', gap: '4px', mt: 0.5 }}>
            {cameraStreamUrls.length === 0 ? (
              <Box sx={{ bgcolor: '#000', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 12, minHeight: 120 }}>Brak strumienia</Box>
            ) : (
              cameraStreamUrls.map((u, idx) => (
                <Box key={`${cameraPlayerEpoch}-${idx}-${u}`} sx={{ minHeight: 0, position: 'relative', bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
                  <HlsPlayer src={u} autoPlay muted live style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }} />
                </Box>
              ))
            )}
          </Box>
          {/* Pasek odtwarzania – widoczny tylko dla trybu Odtwarzaj */}
          {camMode === 'playback' && (
            <Box sx={{ display:'flex', alignItems:'center', gap: 2, mt: 1, p: 1, borderTop: '1px solid #D4D4D9', flexShrink: 0 }}>
              <IconButton size="small" onClick={()=> setIsCamPlaying(v=>!v)} aria-label={isCamPlaying ? 'Pauza' : 'Odtwarzaj'}>
                {isCamPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              <Box sx={{ flex: 1, display:'flex', alignItems:'center', gap: 1 }}>
                <Typography variant="caption" sx={{ width: 40, textAlign: 'right', color:'text.secondary' }}>00:00</Typography>
                <input
                  type="range"
                  min={0}
                  max={1440}
                  value={camMinute}
                  onChange={(e)=> setCamMinute(parseInt(e.target.value,10)||0)}
                  style={{ flex: 1 }}
                />
                <Typography variant="caption" sx={{ width: 40, color:'text.secondary' }}>24:00</Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<Speed />} onClick={cycleSpeed} sx={{ textTransform:'none' }}>{`x${camSpeed}`}</Button>
              <Typography variant="caption" sx={{ color:'text.secondary', ml: 1 }}>{formatMinute(camMinute)}</Typography>
            </Box>
          )}
        </Box>
      )}

        {/* Nagłówek (bez przycisku Dodaj w mobile) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '16.5px', mb: 2 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: '#020242',
              m: 0
            }}
          >
            Projekty
          </Typography>
        </Box>
        {/* Zakładki jak w Google Drive: W realizacji / Zakończone */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: -2.2, mb: 0 }}>
          <Tabs value={mobileTab} onChange={(_, v) => { setMobileTab(v); try { require('../services/realtimeService'); const { getSocket } = require('../services/realtimeService'); getSocket().emit('ui:tab:set', { page: 'projects', tab: v }); } catch {} }} centered>
            <Tab label={`W realizacji (${activeList.length})`} />
            <Tab label={`Zakończone (${closedList.length})`} />
          </Tabs>
        </Box>
        {/* Sync zakładek z obserwowanym użytkownikiem */}
        {(() => { return null; })()}

        {/* Szukaj projektu (po kartach) */}
        <TextField
          size="small"
          placeholder="Szukaj projekt"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
          sx={{
            mt: '7px', mb: 1.5, width: '100%',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }
          }}
        />

        <Box sx={{ borderTop: '1px solid #eee', borderBottom: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
          {mobileProjects.map((p, idx) => (
            <Box key={p.id} sx={{ px: 1.25, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: idx === mobileProjects.length - 1 ? 'none' : '1px solid #eee' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                  {p.id}
                </Typography>
                {/* Avatar kierownika projektu */}
                <Box sx={{ mr: 0.25 }}>
                  <ResponsiblePersonDisplay person={(p as any).responsiblePerson2} label="Kierownik projektu" />
                </Box>
                <IconButton id={`action-button-${p.project_id}`} size="small" onClick={(e)=>handleActionClick(e, p.id)}>
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, color: 'text.secondary' }}>
                <Typography variant="caption">{p.startDate || '—'} — {p.endDate || '—'}</Typography>
                <Typography variant="caption">• Zaaw.: {Math.round(Number(p.progress||0))}%</Typography>
                <Typography variant="caption">• Wartość: {formatK(p.sales)}</Typography>
                <Typography variant="caption">• EBIDTA: {formatK(p.profit)}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Formularze/dialogi */}
        <AddProjectForm
          open={openAddForm}
          onClose={handleCloseAddForm}
          onAdd={handleAddProject}
          onEdit={handleEditProject}
          onDelete={handleDeleteProject}
          editMode={editMode}
          projectData={selectedProject}
        />
        <ImportProjectsDialog open={openImportDialog} onClose={handleCloseImportDialog} onImport={handleImportProjects} />
        {/* Dialogi mobilne: POCR/POKR/Szczegóły podwykonawcy */}
        {pocrOpen && selectedSub && (
          <GeneratePocrDialog
            open={pocrOpen}
            onClose={()=>setPocrOpen(false)}
            subcontractor={selectedSub as any}
            project={selectedProjectMobile as any}
            onSubmit={()=>setPocrOpen(false)}
          />
        )}
        {pokrOpen && selectedSub && (
          <GeneratePokrDialog
            open={pokrOpen}
            onClose={()=>setPokrOpen(false)}
            subcontractor={selectedSub as any}
            project={selectedProjectMobile as any}
            onSubmit={()=>setPokrOpen(false)}
          />
        )}
        {subDetailsOpen && selectedSub && (
          <SubcontractorDetailsDialog
            open={subDetailsOpen}
            onClose={()=>setSubDetailsOpen(false)}
            subcontractor={selectedSub as any}
          />
        )}
        <AddOrderForm
          open={orderFormOpen}
          onClose={()=>setOrderFormOpen(false)}
          onAdd={async (payload:any)=>{ try { await projectService.createOrder(payload); showNotification('Zamówienie dodane', 'success'); setOrderFormOpen(false);} catch(e){ console.error(e); showNotification('Błąd dodawania zamówienia','error'); } }}
          onEdit={()=>{}}
          onDelete={()=>{}}
          editMode={false}
          orderData={{ contractor: orderContractor, order_content: orderContent }}
          currentProjectId={(selectedProjectMobile?.project_id || selectedProjectMobile?.id?.split(' ')?.[0]) as string}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Panel kamery – overlay dla widoku desktopowego */}
      {cameraOpen && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.45)', zIndex: 2147483647 }}>
          <Box sx={{ position:'absolute', top:{ xs: 56, sm: 52 }, right: 0, width: { xs: '100%', md: '50vw' }, height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 52px)' }, bgcolor: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, p: 2, overflow: 'auto', border: '1px solid #D4D4D9', display: 'flex', flexDirection: 'column' }}>
            <IconButton onClick={()=>setCameraOpen(false)} aria-label="Zamknij" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, width: 24, height: 24 }}>
              <Box component="img" src="/assets/icons/close.svg" alt="Zamknij" sx={{ width: 13, height: 13 }} />
            </IconButton>
            {cameraProject && cameraHardwareLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0, pr: 4 }}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Ładowanie urządzeń…</Typography></Box>
            )}
            {cameraProject && !cameraHardwareLoading && cameraHardwareDevices.length === 0 && (
              <Alert severity="info" sx={{ mb: 1, py: 0.5, flexShrink: 0, '& .MuiAlert-message': { py: 0 } }}>Brak urządzenia w Ustawieniach → Hardware dla tego projektu. Używane są domyślne adresy magazynowe.</Alert>
            )}
            {cameraProject && !cameraHardwareLoading && cameraHardwareDevices.length > 0 && !hasHardwareCameras && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.5, flexShrink: 0, '& .MuiAlert-message': { py: 0 } }}>Ustaw „Liczba kamer” większą od zera przy urządzeniu w Ustawieniach → Hardware.</Alert>
            )}
            {/* Ramki wyboru trybu i daty */}
            <Box sx={{ width: '100%', mb: 1, display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                <Box
                  onClick={(e)=> setModeMenuAnchor(e.currentTarget)}
                  sx={{
                    width: 300,
                    height: 36,
                    border: '1px solid #D4D4D9',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    bgcolor: '#fff',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#595966' }
                  }}
                >
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{camMode === 'live' ? 'Na żywo' : 'Odtwarzaj'}</Typography>
                  <Box component="img" src="/assets/icons/chevron-down-small.svg" alt="▼" sx={{ width: 9, height: 5, ml: 1 }} />
                </Box>
                <TextField
                  size="small"
                  type="date"
                  placeholder="dd.mm.rrrr"
                  value={camDate}
                  inputRef={camDateRef}
                  onChange={(e)=> setCamDate(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="img" src="/assets/icons/calendar-small.svg" alt="Kal" sx={{ width: 13, height: 15, cursor: camMode==='live' ? 'default' : 'pointer', opacity: camMode==='live' ? 0.5 : 1 }} onClick={()=> camMode==='live' ? null : (camDateRef.current?.showPicker?.() || camDateRef.current?.focus())} />
                      </InputAdornment>
                    ),
                    endAdornment: null
                  }}
                  sx={{
                    width: 200,
                    '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4D4D9' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                    '& .MuiInputBase-input': { color: camMode==='live' ? '#9e9e9e' : '#2D2D32' },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': { display: 'none' },
                    '& input[type="date"]::-webkit-inner-spin-button': { display: 'none' },
                    '& input[type="date"]::-webkit-clear-button': { display: 'none' }
                  }}
                  disabled={camMode==='live'}
                />
                <Menu anchorEl={modeMenuAnchor} open={Boolean(modeMenuAnchor)} onClose={()=>setModeMenuAnchor(null)}>
                  <MenuItem onClick={()=>{ setCamMode('live'); setModeMenuAnchor(null); }}>Na żywo</MenuItem>
                  <MenuItem onClick={()=>{ setCamMode('playback'); setModeMenuAnchor(null); }}>Odtwarzaj</MenuItem>
                </Menu>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `repeat(${cameraGridCols}, 1fr)`, gridAutoRows: '1fr', gap: '4px', mt: 0.5 }}>
              {cameraStreamUrls.length === 0 ? (
                <Box sx={{ bgcolor: '#000', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 12, minHeight: 120 }}>Brak strumienia</Box>
              ) : (
                cameraStreamUrls.map((u, idx) => (
                  <Box key={`${cameraPlayerEpoch}-${idx}-${u}`} sx={{ minHeight: 0, position: 'relative', bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
                    <HlsPlayer src={u} autoPlay muted live style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }} />
                  </Box>
                ))
              )}
            </Box>
            {camMode === 'playback' && (
              <Box sx={{ display:'flex', alignItems:'center', gap: 2, mt: 1, p: 1, borderTop: '1px solid #D4D4D9', flexShrink: 0 }}>
                <IconButton size="small" onClick={()=> setIsCamPlaying(v=>!v)} aria-label={isCamPlaying ? 'Pauza' : 'Odtwarzaj'}>
                  {isCamPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <Box sx={{ flex: 1, display:'flex', alignItems:'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ width: 40, textAlign: 'right', color:'text.secondary' }}>00:00</Typography>
                  <input
                    type="range"
                    min={0}
                    max={1440}
                    value={camMinute}
                    onChange={(e)=> setCamMinute(parseInt(e.target.value,10)||0)}
                    style={{ flex: 1 }}
                  />
                  <Typography variant="caption" sx={{ width: 40, color:'text.secondary' }}>24:00</Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<Speed />} onClick={cycleSpeed} sx={{ textTransform:'none' }}>{`x${camSpeed}`}</Button>
                <Typography variant="caption" sx={{ color:'text.secondary', ml: 1 }}>{formatMinute(camMinute)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* Menu akcji dla projektów */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => handleActionSelect('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj</ListItemText>
        </MenuItem>
        {/* DWG/DXF – dostępne pod ikoną CAD (architecture) w tabeli */}
      </Menu>

      {/* Menu zmiany fazy projektu */}
      <Menu
        anchorEl={phaseMenuAnchorEl}
        open={Boolean(phaseMenuAnchorEl)}
        onClose={handlePhaseMenuClose}
      >
        <MenuItem onClick={() => handlePhaseChange('W REALIZACJI')}>
          W REALIZACJI
        </MenuItem>
        <MenuItem onClick={() => handlePhaseChange('ZAKOŃCZONE W OKRESIE GWARANCJI')}>
          ZAKOŃCZONE W OKRESIE GWARANCJI
        </MenuItem>
        <MenuItem onClick={() => handlePhaseChange('ZAKOŃCZONE PO GWARANCJI')}>
          ZAKOŃCZONE PO GWARANCJI
        </MenuItem>
      </Menu>
      {/* Formularz dodawania/edycji projektu */}
      <AddProjectForm
        open={openAddForm}
        onClose={handleCloseAddForm}
        onAdd={handleAddProject}
        onEdit={handleEditProject}
        onDelete={handleDeleteProject}
        editMode={editMode}
        projectData={selectedProject}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Projekty
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            onClick={handleOpenAddForm}
            data-action="add"
            data-hotkey-add="true"
            sx={{ 
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: '#4040DB', // Property 1=Default
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' }, // Property 1=Variant2 (ciemniejszy hover)
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj projekt
          </Button>
        </Box>
      </Box>

      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '300px 300px 200px 200px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Search 300x36, white bg, #d4d4d9 border, 20px icon */}
        <TextField
          size="small"
          placeholder="Szukaj w projektach"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { 
              if (e.key === 'Escape') { 
                e.preventDefault(); 
                e.stopPropagation(); 
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {} 
              } 
            } 
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Group: Kierownik + Select 300x36, label 16px height */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Kierownik projektu</Typography>
          <Select
            size="small"
            value={manager}
            onChange={handleManagerChange}
            displayEmpty
            renderValue={manager !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png" // umieść plik w public/assets/filters
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: manager ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszyscy</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.user_id} value={u.display_name || u.email || u.user_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                  <Box component="span">{u.display_name || u.email}</Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Group: Data od 200x36 */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            variant="outlined"
            inputRef={startDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (startDateRef.current as any)?.showPicker?.(); } catch { startDateRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: startDate ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Group: Data do 200x36 */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            variant="outlined"
            inputRef={endDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (endDateRef.current as any)?.showPicker?.(); } catch { endDateRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: endDate ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="project tabs"
          sx={{ 
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          <Tab 
            disableRipple
            label={`W realizacji (${tabCounts['W REALIZACJI']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab 
            disableRipple
            label={`Zakończone w okresie gwarancji (${tabCounts['ZAKOŃCZONE W OKRESIE GWARANCJI']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab 
            disableRipple
            label={`Zakończone po gwarancji (${tabCounts['ZAKOŃCZONE PO GWARANCJI']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
        </Tabs>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            mt: '4px',
            p: 0,
            alignSelf: 'flex-end',
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e)=>{ 
            // Jeden klik: włącz tryb zmiany szerokości i pokaż okno
            setColResizeMode(true);
            setColsAnchorEl(e.currentTarget);
          }}
        >
          <Box
            component="img"
            alt="kolumny"
            src={'/assets/icons/frame-344.svg?v=2'}
            sx={{ width: 32, height: 32 }}
          />
        </IconButton>
      </Box>

      {/* Okno „Dostosuj kolumny" – bez przyciemniania tła, aby umożliwić edycję szerokości */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
      >
        <Box
          sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
          onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
        >
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
          <IconButton size="small" onClick={()=>{ setColsAnchorEl(null); setColResizeMode(false); setDraftColumnsVisible(null); }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {(() => {
            // przygotuj draft jeśli nie istnieje
            const list = draftColumnsVisible ?? columnsVisible;
            if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
            const ord = draftOrder ?? columnOrder;
            if (draftOrder == null) setDraftOrder([...columnOrder]);
            return ord.map((key) => (
            <Box key={key} sx={{ py: 1, px: 2 }}
              draggable
              onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
              onDragOver={(e)=>{ e.preventDefault(); }}
              onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
            >
              <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                    <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                  </IconButton>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{key.toUpperCase()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  <Box
                    sx={{
                      width: '4px',
                      height: '20px',
                      backgroundColor: colResizeMode ? '#2020AF' : '#FF0000',
                      cursor: 'col-resize',
                      borderRadius: '2px',
                      flexShrink: 0
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragCol({ 
                        key, 
                        startX: e.clientX, 
                        startW: parseInt(getColWidthPx(key)) || (columnWidths[key] ?? defaultColumnWidths[key] ?? 40) 
                      });
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )); })()}
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
          <Button variant="text" onClick={()=>{ setColsAnchorEl(null); setColResizeMode(false); setDraftColumnsVisible(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ if (draftColumnsVisible) { setColumnsVisible(draftColumnsVisible); try { const payload = JSON.stringify(draftColumnsVisible); localStorage.setItem(VISIBLE_KEY, payload); localStorage.setItem(VISIBLE_KEY_GLOBAL, payload); } catch {} } if (draftOrder) { const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); setColumnOrder(normalized); try { const payload = JSON.stringify(normalized); localStorage.setItem(ORDER_KEY, payload); localStorage.setItem(ORDER_KEY_GLOBAL, payload); } catch {} } setColsAnchorEl(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
        </Box>
        </Paper>
      </Backdrop>

      <TabPanel value={tabValue} index={0}>
        <TableContainer ref={tableContainerRef} component={Paper} sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', overflowY: 'auto', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
          {!settingsReady ? (
            <Box sx={{ height: 432 }} />
          ) : (
          <Table size="small" stickyHeader sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
            '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}>
            {renderColGroup(draftOrder ?? columnOrder)}
            <TableHead>
              <TableRow>
                {/* Kolumna zaznaczeń usunięta */}
                {(draftOrder ?? columnOrder).map((key) => (
                  colV(key) ? renderResizableTh(key, (
                    key === 'project' ? (
                  <TableSortLabel {...getSortLabelProps(orderBy === 'project')} active={orderBy === 'project'} direction={orderBy === 'project' ? order : 'asc'} onClick={() => handleRequestSort('project')}>
                    Projekt
                  </TableSortLabel>
                    ) : key === 'kp' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'kp')} active={orderBy === 'kp'} direction={orderBy === 'kp' ? order : 'asc'} onClick={() => handleRequestSort('kp')}>KP</TableSortLabel>)
                    : key === 'days' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'days')} active={orderBy === 'days'} direction={orderBy === 'days' ? order : 'asc'} onClick={() => handleRequestSort('days')}>Dni</TableSortLabel>)
                    : key === 'progress' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'progress')} active={orderBy === 'progress'} direction={orderBy === 'progress' ? order : 'asc'} onClick={() => handleRequestSort('progress')}>ZAAW</TableSortLabel>)
                    : key === 'wwp' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'wwp')} active={orderBy === 'wwp'} direction={orderBy === 'wwp' ? order : 'asc'} onClick={() => handleRequestSort('wwp')}>WWP</TableSortLabel>)
                    : key === 'wwh' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'wwh')} active={orderBy === 'wwh'} direction={orderBy === 'wwh' ? order : 'asc'} onClick={() => handleRequestSort('wwh')}>WWH</TableSortLabel>)
                    : key === 'value' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'value')} active={orderBy === 'value'} direction={orderBy === 'value' ? order : 'asc'} onClick={() => handleRequestSort('value')}>Wartość</TableSortLabel>)
                    : key === 'sales' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'sales')} active={orderBy === 'sales'} direction={orderBy === 'sales' ? order : 'asc'} onClick={() => handleRequestSort('sales')}>Sprzedaż</TableSortLabel>)
                    : key === 'pdz' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'pdz')} active={orderBy === 'pdz'} direction={orderBy === 'pdz' ? order : 'asc'} onClick={() => handleRequestSort('pdz')}>PDZ</TableSortLabel>)
                    : key === 'budget' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'budget')} active={orderBy === 'budget'} direction={orderBy === 'budget' ? order : 'asc'} onClick={() => handleRequestSort('budget')}>Budżet</TableSortLabel>)
                    : key === 'costs' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'costs')} active={orderBy === 'costs'} direction={orderBy === 'costs' ? order : 'asc'} onClick={() => handleRequestSort('costs')}>Koszty</TableSortLabel>)
                    : key === 'profit' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'profit')} active={orderBy === 'profit'} direction={orderBy === 'profit' ? order : 'asc'} onClick={() => handleRequestSort('profit')}>EBIDTA</TableSortLabel>)
                    : key === 'ros' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'ros')} active={orderBy === 'ros'} direction={orderBy === 'ros' ? order : 'asc'} onClick={() => handleRequestSort('ros')}>C.ROS</TableSortLabel>)
                    : ''
                  ), ['value','sales','pdz','budget','costs','profit'].includes(key) ? 'right' : undefined) : null
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProjects.map((project, index) => {
                const isItemSelected = isSelected(project.id + index);
                return (
                  <TableRow
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#F5F5FF !important' },
                      '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                      '&.Mui-selected': { backgroundColor: '#F5F5FF !important' },
                      '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' }
                    }}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={project.id + index}
                    selected={isItemSelected}
                    data-project-id={String((project as any).project_id || project.id)}
                  >
                    {/* Kolumna checkbox usunięta */}
                    {(draftOrder ?? columnOrder).map((key) => colV(key) ? renderDataCellMain(key, project, index) : null)}
                  </TableRow>
                );
              })}
              {/* Wiersz podsumowania – dynamiczny układ i widoczność */}
              {(() => {
                const currentOrder = (draftOrder ?? columnOrder);
                const visibleKeys = currentOrder.filter(k => colV(k));
                const sumKeys = ['value','sales','pdz','budget','costs','profit'];
                const firstVisibleSumIndex = visibleKeys.findIndex(k => sumKeys.includes(k));
                const beforeCount = firstVisibleSumIndex < 0 ? visibleKeys.length : firstVisibleSumIndex;
                const sumFor = (key: string) => {
                  switch (key) {
                    case 'value':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.value === 'number' ? p.value : (typeof p.value === 'string' ? parseFloat(p.value) : 0)), 0) / 1000);
                    case 'sales':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.sales === 'number' ? p.sales : (typeof p.sales === 'string' ? parseFloat(p.sales) : 0)), 0) / 1000);
                    case 'pdz':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.PDZ === 'number' ? p.PDZ : (typeof p.PDZ === 'string' ? parseFloat(p.PDZ) : 0)), 0) / 1000);
                    case 'budget':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.planBud === 'number' ? p.planBud : (typeof p.planBud === 'string' ? parseFloat(p.planBud) : 0)), 0) / 1000);
                    case 'costs':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.costs === 'number' ? p.costs : (typeof p.costs === 'string' ? parseFloat(p.costs) : 0)), 0) / 1000);
                    case 'profit':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.profit === 'number' ? p.profit : (typeof p.profit === 'string' ? parseFloat(p.profit) : 0)), 0) / 1000);
                    default:
                      return 0;
                  }
                };
                const cellsAfter = [] as React.ReactNode[];
                visibleKeys.slice(beforeCount).forEach((k: string) => {
                  if (sumKeys.includes(k)) {
                    cellsAfter.push(
                      <TableCell key={`sum-${k}`} align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>{sumFor(k)}</TableCell>
                    );
                  } else {
                    cellsAfter.push(
                      <TableCell key={`empty-${k}`} sx={{ backgroundColor: '#F1F1F6' }} />
                    );
                  }
                });
                return (
                  <TableRow data-sum-row={true} sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                    {beforeCount > 0 && (
                      <TableCell colSpan={beforeCount} align="right" sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>Suma:</TableCell>
                    )}
                    {cellsAfter}
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
          )}
          <Box sx={{ position: 'absolute', bottom: -6, right: -4 }}>
            {/* Ikona koła zębatego usunięta – funkcję przejmuje przycisk u góry */}
          </Box>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper} sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', overflowY: 'auto', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
          {!settingsReady ? (
            <Box sx={{ height: 432 }} />
          ) : (
          <Table size="small" stickyHeader sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
            '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}>
            {renderColGroup(draftOrder ?? columnOrder)}
            <TableHead>
              <TableRow>
                {(draftOrder ?? columnOrder).map((key) => (
                  colV(key) ? renderResizableTh(key, (
                    key === 'project' ? (
                      <TableSortLabel {...getSortLabelProps(orderBy === 'project')} active={orderBy === 'project'} direction={orderBy === 'project' ? order : 'asc'} onClick={() => handleRequestSort('project')}>
                        Projekt
                      </TableSortLabel>
                    ) : key === 'kp' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'kp')} active={orderBy === 'kp'} direction={orderBy === 'kp' ? order : 'asc'} onClick={() => handleRequestSort('kp')}>KP</TableSortLabel>)
                    : key === 'days' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'days')} active={orderBy === 'days'} direction={orderBy === 'days' ? order : 'asc'} onClick={() => handleRequestSort('days')}>Dni</TableSortLabel>)
                    : key === 'progress' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'progress')} active={orderBy === 'progress'} direction={orderBy === 'progress' ? order : 'asc'} onClick={() => handleRequestSort('progress')}>ZAAW</TableSortLabel>)
                    : key === 'wwp' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'wwp')} active={orderBy === 'wwp'} direction={orderBy === 'wwp' ? order : 'asc'} onClick={() => handleRequestSort('wwp')}>WWP</TableSortLabel>)
                    : key === 'wwh' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'wwh')} active={orderBy === 'wwh'} direction={orderBy === 'wwh' ? order : 'asc'} onClick={() => handleRequestSort('wwh')}>WWH</TableSortLabel>)
                    : key === 'value' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'value')} active={orderBy === 'value'} direction={orderBy === 'value' ? order : 'asc'} onClick={() => handleRequestSort('value')}>Wartość</TableSortLabel>)
                    : key === 'sales' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'sales')} active={orderBy === 'sales'} direction={orderBy === 'sales' ? order : 'asc'} onClick={() => handleRequestSort('sales')}>Sprzedaż</TableSortLabel>)
                    : key === 'pdz' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'pdz')} active={orderBy === 'pdz'} direction={orderBy === 'pdz' ? order : 'asc'} onClick={() => handleRequestSort('pdz')}>PDZ</TableSortLabel>)
                    : key === 'budget' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'budget')} active={orderBy === 'budget'} direction={orderBy === 'budget' ? order : 'asc'} onClick={() => handleRequestSort('budget')}>Budżet</TableSortLabel>)
                    : key === 'costs' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'costs')} active={orderBy === 'costs'} direction={orderBy === 'costs' ? order : 'asc'} onClick={() => handleRequestSort('costs')}>Koszty</TableSortLabel>)
                    : key === 'profit' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'profit')} active={orderBy === 'profit'} direction={orderBy === 'profit' ? order : 'asc'} onClick={() => handleRequestSort('profit')}>EBIDTA</TableSortLabel>)
                    : key === 'ros' ? (<TableSortLabel {...getSortLabelProps(orderBy === 'ros')} active={orderBy === 'ros'} direction={orderBy === 'ros' ? order : 'asc'} onClick={() => handleRequestSort('ros')}>C.ROS</TableSortLabel>)
                    : ''
                  ), ['value','sales','pdz','budget','costs','profit'].includes(key) ? 'right' : undefined) : null
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProjects.map((project, index) => {
                const isItemSelected = isSelected(project.id + index);
                return (
                  <TableRow
                    hover
                    sx={{ '&:hover': { backgroundColor: '#F5F5FF' } }}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={project.id + index}
                    selected={isItemSelected}
                    data-project-id={String((project as any).project_id || project.id)}
                  >
                    {(draftOrder ?? columnOrder).map((key) => colV(key) ? renderDataCellMain(key, project, index) : null)}
                  </TableRow>
                );
              })}
              {/* Wiersz podsumowania – dynamiczny układ i widoczność (jak w "W realizacji") */}
              {(() => {
                const currentOrder: string[] = (draftOrder ?? columnOrder);
                const visibleKeys: string[] = currentOrder.filter((k: string) => colV(k));
                const sumKeys: string[] = ['value','sales','pdz','budget','costs','profit'];
                const firstVisibleSumIndex = visibleKeys.findIndex((k: string) => sumKeys.includes(k));
                const beforeCount = firstVisibleSumIndex < 0 ? visibleKeys.length : firstVisibleSumIndex;
                const sumFor = (key: string) => {
                  switch (key) {
                    case 'value':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.value === 'number' ? p.value : (typeof p.value === 'string' ? parseFloat(p.value) : 0)), 0) / 1000);
                    case 'sales':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.sales === 'number' ? p.sales : (typeof p.sales === 'string' ? parseFloat(p.sales) : 0)), 0) / 1000);
                    case 'pdz':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.PDZ === 'number' ? p.PDZ : (typeof p.PDZ === 'string' ? parseFloat(p.PDZ) : 0)), 0) / 1000);
                    case 'budget':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.planBud === 'number' ? p.planBud : (typeof p.planBud === 'string' ? parseFloat(p.planBud) : 0)), 0) / 1000);
                    case 'costs':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.costs === 'number' ? p.costs : (typeof p.costs === 'string' ? parseFloat(p.costs) : 0)), 0) / 1000);
                    case 'profit':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.profit === 'number' ? p.profit : (typeof p.profit === 'string' ? parseFloat(p.profit) : 0)), 0) / 1000);
                    default:
                      return 0;
                  }
                };
                const cellsAfter = [] as React.ReactNode[];
                visibleKeys.slice(beforeCount).forEach((k: string) => {
                  if (sumKeys.includes(k)) {
                    cellsAfter.push(
                      <TableCell key={`sum-${k}`} align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>{sumFor(k)}</TableCell>
                    );
                  } else {
                    cellsAfter.push(
                      <TableCell key={`empty-${k}`} sx={{ backgroundColor: '#F1F1F6' }} />
                    );
                  }
                });
                return (
                  <TableRow data-sum-row={true} sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                    {beforeCount > 0 && (
                      <TableCell colSpan={beforeCount} align="right" sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>Suma:</TableCell>
                    )}
                    {cellsAfter}
                  </TableRow>
                );
              })()}
              </TableBody>
          </Table>
          )}
          <Box sx={{ position: 'absolute', bottom: -6, right: -4 }}>
            {/* Ikona koła zębatego usunięta – funkcję przejmuje przycisk u góry */}
          </Box>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper} sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', overflowY: 'auto', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
          {!settingsReady ? (
            <Box sx={{ height: 432 }} />
          ) : (
          <Table size="small" stickyHeader sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
            '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}>
            {renderColGroup(draftOrder ?? columnOrder)}
            <TableHead>
              <TableRow>
                {(draftOrder ?? columnOrder).map((key) => (
                  colV(key) ? renderResizableTh(
                    key,
                    (
                      key === 'project' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'project')} active={orderBy === 'project'} direction={orderBy === 'project' ? order : 'asc'}>Projekt</TableSortLabel>
                      ) : key === 'kp' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'kp')} active={orderBy === 'kp'} direction={orderBy === 'kp' ? order : 'asc'}>KP</TableSortLabel>
                      ) : key === 'days' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'days')} active={orderBy === 'days'} direction={orderBy === 'days' ? order : 'asc'}>Dni</TableSortLabel>
                      ) : key === 'progress' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'progress')} active={orderBy === 'progress'} direction={orderBy === 'progress' ? order : 'asc'}>ZAAW</TableSortLabel>
                      ) : key === 'wwp' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'wwp')} active={orderBy === 'wwp'} direction={orderBy === 'wwp' ? order : 'asc'}>WWP</TableSortLabel>
                      ) : key === 'wwh' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'wwh')} active={orderBy === 'wwh'} direction={orderBy === 'wwh' ? order : 'asc'}>WWH</TableSortLabel>
                      ) : key === 'value' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'value')} active={orderBy === 'value'} direction={orderBy === 'value' ? order : 'asc'}>Wartość</TableSortLabel>
                      ) : key === 'sales' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'sales')} active={orderBy === 'sales'} direction={orderBy === 'sales' ? order : 'asc'}>Sprzedaż</TableSortLabel>
                      ) : key === 'pdz' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'pdz')} active={orderBy === 'pdz'} direction={orderBy === 'pdz' ? order : 'asc'}>PDZ</TableSortLabel>
                      ) : key === 'budget' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'budget')} active={orderBy === 'budget'} direction={orderBy === 'budget' ? order : 'asc'}>Budżet</TableSortLabel>
                      ) : key === 'costs' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'costs')} active={orderBy === 'costs'} direction={orderBy === 'costs' ? order : 'asc'}>Koszty</TableSortLabel>
                      ) : key === 'profit' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'profit')} active={orderBy === 'profit'} direction={orderBy === 'profit' ? order : 'asc'}>EBIDTA</TableSortLabel>
                      ) : key === 'ros' ? (
                        <TableSortLabel {...getSortLabelProps(orderBy === 'ros')} active={orderBy === 'ros'} direction={orderBy === 'ros' ? order : 'asc'}>C.ROS</TableSortLabel>
                      ) : (
                        ''
                      )
                    ),
                    ['value','sales','pdz','budget','costs','profit'].includes(key) ? 'right' : undefined
                  ) : null
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProjects.map((project, index) => {
                const isItemSelected = isSelected(project.id + index);
                return (
                  <TableRow
                    hover
                    sx={{ '&:hover': { backgroundColor: '#F5F5FF' } }}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={project.id + index}
                    selected={isItemSelected}
                    data-project-id={String((project as any).project_id || project.id)}
                  >
                    {(draftOrder ?? columnOrder).map((key) => colV(key) ? renderDataCellMain(key, project, index) : null)}
                  </TableRow>
                );
              })}
              {/* Wiersz podsumowania – dynamiczny układ i widoczność (jak w "W realizacji") */}
              {(() => {
                const currentOrder: string[] = (draftOrder ?? columnOrder);
                const visibleKeys: string[] = currentOrder.filter((k: string) => colV(k));
                const sumKeys: string[] = ['value','sales','pdz','budget','costs','profit'];
                const firstVisibleSumIndex = visibleKeys.findIndex((k: string) => sumKeys.includes(k));
                const beforeCount = firstVisibleSumIndex < 0 ? visibleKeys.length : firstVisibleSumIndex;
                const sumFor = (key: string) => {
                  switch (key) {
                    case 'value':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.value === 'number' ? p.value : (typeof p.value === 'string' ? parseFloat(p.value) : 0)), 0) / 1000);
                    case 'sales':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.sales === 'number' ? p.sales : (typeof p.sales === 'string' ? parseFloat(p.sales) : 0)), 0) / 1000);
                    case 'pdz':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.PDZ === 'number' ? p.PDZ : (typeof p.PDZ === 'string' ? parseFloat(p.PDZ) : 0)), 0) / 1000);
                    case 'budget':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.planBud === 'number' ? p.planBud : (typeof p.planBud === 'string' ? parseFloat(p.planBud) : 0)), 0) / 1000);
                    case 'costs':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.costs === 'number' ? p.costs : (typeof p.costs === 'string' ? parseFloat(p.costs) : 0)), 0) / 1000);
                    case 'profit':
                      return Math.round(filteredProjects.reduce((sum, p) => sum + (typeof p.profit === 'number' ? p.profit : (typeof p.profit === 'string' ? parseFloat(p.profit) : 0)), 0) / 1000);
                    default:
                      return 0;
                  }
                };
                const cellsAfter = [] as React.ReactNode[];
                visibleKeys.slice(beforeCount).forEach((k: string) => {
                  if (sumKeys.includes(k)) {
                    cellsAfter.push(
                      <TableCell key={`sum-${k}`} align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>{sumFor(k)}</TableCell>
                    );
                  } else {
                    cellsAfter.push(
                      <TableCell key={`empty-${k}`} sx={{ backgroundColor: '#F1F1F6' }} />
                    );
                  }
                });
                return (
                  <TableRow data-sum-row={true} sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
                    {beforeCount > 0 && (
                      <TableCell colSpan={beforeCount} align="right" sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>Suma:</TableCell>
                    )}
                    {cellsAfter}
                  </TableRow>
                );
              })()}
              </TableBody>
          </Table>
          )}
          <Box sx={{ position: 'absolute', bottom: -6, right: -4 }}>
            {/* Ikona koła zębatego usunięta – funkcję przejmuje przycisk u góry */}
          </Box>
        </TableContainer>
      </TabPanel>

      {/* Dolny panel szczegółów usunięty – szczegóły dostępne na /projects/:id */}

      {/* Dialog importu projektów */}
      <ImportProjectsDialog
        open={openImportDialog}
        onClose={handleCloseImportDialog}
        onImport={handleImportProjects}
      />

      {/* Listener do synchronizacji zakładek z obserwowanym użytkownikiem (renderowana kotwica, logika w hooku wyżej) */}
    </Box>
  );
};

export default ProjectsPage;
